import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import {
  Mail,
  Plus,
  Pencil,
  Trash2,
  GripVertical,
  Save,
  X,
  ArrowLeft,
  Download,
  ToggleLeft,
  ToggleRight,
  AlertCircle,
  CheckCircle,
  Sparkles,
  FlaskConical,
  Loader2,
  Table2,
  Star,
  FileText,
  Edit3,
  Truck,
  Wallet,
  MessageSquare,
  Paintbrush,
  Palette,
  ShieldAlert,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Upload,
  Copy,
  Check,
  Layers,
  ArrowUp,
  RefreshCw,
  FileUp,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  adminApi,
  labApi,
  superAdminApi,
  type SystemEmailTemplate,
  type SystemMessageTemplateCategory,
  type SystemAsset,
  type PageTemplateAdminRow,
} from '../lib/api';
import { plainTextPasteModules, withPlainTextPaste } from '../lib/quillPlainTextPaste';
import { useAuthStore } from '../store/auth';
import { SuperAdminAiContent } from './SuperAdminAiPage';
import { SuperAdminFinanceCollectorContent } from './SuperAdminFinanceCollectorPage';
import { BrandingContent } from './BrandingPage';
import { EmptyState } from '../components/EmptyState';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { Select } from '../components/ui/Select';
import { MessageTemplateVariablePicker } from '../components/messages/MessageTemplateVariablePicker';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { AdminKreatorDefaultsPage } from './AdminKreatorDefaultsPage';
import { TEMPLATE_CATEGORIES, normalizeTemplateCategory, templateCategoryLabel } from './kreator/builder-v2/templateCategories';
import {
  ProductShortDescriptionBuilderSection,
  normalizeProductShortDescriptionElements,
  type ProductShortDescriptionElement,
} from './kreator/ProductShortDescriptionBuilderSection';
import {
  ProductAdditionalElementsSection,
  normalizeProductAdditionalElements,
  type ProductAdditionalElement,
} from './kreator/ProductAdditionalElementsSection';
import { CHATGPT_TEXT_MODELS, KIMI_TEXT_MODELS } from '../config/aiTextModels';

// ─── Toast helper ────────────────────────────────────────────────────────────
type ToastType = 'success' | 'error';
interface Toast {
  id: number;
  type: ToastType;
  message: string;
}

let toastCounter = 0;

function useToasts() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const addToast = useCallback((type: ToastType, message: string) => {
    const id = ++toastCounter;
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);

  return { toasts, addToast };
}

function ToastContainer({ toasts }: { toasts: Toast[] }) {
  if (toasts.length === 0) return null;
  return (
    <div className="fixed top-20 right-6 z-50 space-y-2">
      {toasts.map(t => (
        <div
          key={t.id}
          className={`flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg text-sm font-medium animate-slide-up ${
            t.type === 'success'
              ? 'bg-success/20 text-success border border-success/30'
              : 'bg-danger/20 text-danger border border-danger/30'
          }`}
        >
          {t.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {t.message}
        </div>
      ))}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────
type AdminTab = 'branding' | 'logistyka' | 'finanse' | 'wiadomosci' | 'ai' | 'laboratorium' | 'kreator' | 'grafiki' | 'szablony';
const ADMIN_TAB_KEYS: AdminTab[] = ['branding', 'logistyka', 'finanse', 'wiadomosci', 'laboratorium', 'kreator', 'grafiki', 'szablony', 'ai'];

export function AdminPanelPage() {
  const { user } = useAuthStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as AdminTab | null;
  const [adminTab, setAdminTabState] = useState<AdminTab>(
    tabParam && ADMIN_TAB_KEYS.includes(tabParam) ? tabParam : 'branding'
  );
  const setAdminTab = (tab: AdminTab) => {
    setAdminTabState(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  // Block non-SUPER_ADMIN users entirely
  if (user?.role !== 'SUPER_ADMIN') {
    return <Navigate to="/" replace />;
  }

  const tabs: { key: AdminTab; label: string; icon: React.ReactNode }[] = [
    { key: 'branding', label: 'Branding', icon: <Palette className="w-4 h-4" /> },
    { key: 'logistyka', label: 'Logistyka', icon: <Truck className="w-4 h-4" /> },
    { key: 'finanse', label: 'Finanse', icon: <Wallet className="w-4 h-4" /> },
    { key: 'wiadomosci', label: 'Wiadomości', icon: <MessageSquare className="w-4 h-4" /> },
    { key: 'laboratorium', label: 'Laboratorium', icon: <FlaskConical className="w-4 h-4" /> },
    { key: 'kreator', label: 'Kreator', icon: <Paintbrush className="w-4 h-4" /> },
    { key: 'grafiki', label: 'Grafiki', icon: <ImageIcon className="w-4 h-4" /> },
    { key: 'szablony', label: 'Szablony', icon: <Layers className="w-4 h-4" /> },
    { key: 'ai', label: 'AI', icon: <Sparkles className="w-4 h-4" /> },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="text-3xl font-bold text-text-primary">Panel administratora</h1>
        <p className="text-text-secondary mt-2">
          Zarządzaj globalnymi ustawieniami aplikacji
        </p>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 bg-surface-raised/50 p-1 rounded-xl border border-border-subtle w-fit animate-slide-up">
        {tabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setAdminTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              adminTab === tab.key
                ? 'bg-accent-primary/20 text-accent-primary border border-accent-primary/30'
                : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50'
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {adminTab === 'branding' && <BrandingContent />}
      {adminTab === 'logistyka' && <AdminPanelContent />}
      {adminTab === 'finanse' && <SuperAdminFinanceCollectorContent />}
      {adminTab === 'wiadomosci' && <AdminMessagesContent />}
      {adminTab === 'ai' && <SuperAdminAiContent />}
      {adminTab === 'laboratorium' && <AdminLabContent />}
      {adminTab === 'kreator' && <AdminKreatorDefaultsPage />}
      {adminTab === 'grafiki' && <AdminSystemAssetsContent />}
      {adminTab === 'szablony' && <AdminPageTemplatesContent />}
    </div>
  );
}

// ─── Admin Wiadomości (system message templates for compose/reply) ────────────
interface SystemMessageTemplate {
  id: string;
  name: string;
  categoryId: string | null;
  subject: string | null;
  bodyHtml: string;
  bodyText: string | null;
  shortcut: string | null;
  autoSend: boolean;
  isActive: boolean;
  position: number;
}

type AdminMessagesSubTab = 'templates' | 'rodo';

const ADMIN_MESSAGES_SUBTABS: { key: AdminMessagesSubTab; label: string; icon: React.ReactNode }[] = [
  { key: 'templates', label: 'Szablony szybkich odpowiedzi', icon: <FileText className="w-3.5 h-3.5" /> },
  { key: 'rodo', label: 'RODO — purge maili klienta', icon: <ShieldAlert className="w-3.5 h-3.5" /> },
];

function AdminMessagesContent() {
  const [subTab, setSubTab] = useState<AdminMessagesSubTab>('templates');
  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex gap-1 bg-surface-raised rounded-lg p-1 border border-border overflow-x-auto max-w-full">
        {ADMIN_MESSAGES_SUBTABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setSubTab(tab.key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
              subTab === tab.key ? 'bg-surface-hover text-text-primary' : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {subTab === 'templates' && <AdminMessageTemplatesContent />}
      {subTab === 'rodo' && <AdminInboxPurgeContent />}
    </div>
  );
}

// ─── RODO purge (cross-tenant, SUPER_ADMIN only z re-auth) ──────────────────
function AdminInboxPurgeContent() {
  const [userEmail, setUserEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [purging, setPurging] = useState(false);
  const [lastResult, setLastResult] = useState<{ tenantName: string; bodies: number; snippets: number } | null>(null);
  const { toasts, addToast } = useToasts();

  const canSubmit = userEmail.trim().length > 3 && password.length > 0 && !purging;

  const handlePurge = async () => {
    setPurging(true);
    try {
      const { data } = await adminApi.inboxPurge({
        userEmail: userEmail.trim(),
        password,
      });
      setLastResult({
        tenantName: data.tenant.name,
        bodies: data.bodiesDeleted,
        snippets: data.snippetsCleared,
      });
      addToast(
        'success',
        `Wyczyszczono ${data.bodiesDeleted} body i ${data.snippetsCleared} snippetów dla ${data.tenant.name}`,
      );
      setPassword('');
      setConfirmOpen(false);
    } catch (err) {
      const msg =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'Nie udało się wyczyścić treści';
      addToast('error', msg);
      setConfirmOpen(false);
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="space-y-4 animate-slide-up">
      <ToastContainer toasts={toasts} />

      <div className="card p-5 border border-danger/30 bg-danger/5">
        <div className="flex items-start gap-3 pb-4 border-b border-border">
          <div className="p-2 rounded-lg bg-danger/10 flex-shrink-0">
            <ShieldAlert className="w-5 h-5 text-danger" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-text-primary">RODO purge — wyczyść treść maili klienta</h2>
            <p className="text-xs text-text-secondary mt-1">
              Usuwa treść (body + snippet) wszystkich maili tenanta zidentyfikowanego po
              emailu jego użytkownika. Lista maili (nadawca, temat, data) zostaje. Operacja
              jest logowana w dzienniku audytu (<code className="text-text-secondary">inbox_purge_logs</code>).
            </p>
          </div>
        </div>

        <div className="space-y-4 mt-4">
          {/* Login klienta */}
          <div>
            <label className="block text-xs text-text-secondary mb-1.5">
              Email użytkownika klienta (system znajdzie tenanta po tym emailu)
            </label>
            <input
              type="email"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
              placeholder="klient@firma.pl"
              autoComplete="off"
              disabled={purging}
              className="w-full bg-surface-hover border border-border rounded-lg px-3 py-2 text-sm text-text-primary focus:border-danger/50 focus:outline-none"
            />
          </div>

          {/* Re-auth: hasło SUPER_ADMINa */}
          <div>
            <label className="block text-xs text-text-secondary mb-1.5">
              Twoje hasło SUPER_ADMINa (potwierdzenie tożsamości)
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                disabled={purging}
                className="w-full bg-surface-hover border border-border rounded-lg px-3 py-2 pr-10 text-sm text-text-primary focus:border-danger/50 focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-secondary hover:text-text-primary transition-colors"
                title={showPassword ? 'Ukryj hasło' : 'Pokaż hasło'}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-[11px] text-text-muted mt-1">
              Backend porównuje bcrypt-em z hashem w bazie. Hasło nigdzie nie jest logowane.
            </p>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={() => setConfirmOpen(true)}
              disabled={!canSubmit}
              className="btn-primary text-sm flex items-center gap-1.5 bg-danger hover:bg-danger border-danger disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Wyczyść treść maili tego klienta
            </button>
            {lastResult && (
              <span className="text-xs text-text-secondary">
                Ostatnio: <strong className="text-text-primary">{lastResult.tenantName}</strong> — {lastResult.bodies} body, {lastResult.snippets} snippetów
              </span>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Wyczyścić treść maili klienta?"
        message={
          <>
            Operacja usunie treść (body + snippet) <strong>wszystkich maili</strong> tenanta
            powiązanego z emailem <code className="text-text-primary">{userEmail}</code>. Lista maili zostaje, ale
            treści nie da się odzyskać.
          </>
        }
        description="Operacja zostanie zalogowana w dzienniku audytu RODO (inbox_purge_logs)."
        confirmLabel="Wyczyść teraz"
        onConfirm={handlePurge}
        onClose={() => !purging && setConfirmOpen(false)}
        isPending={purging}
      />
    </div>
  );
}

function AdminMessageTemplatesContent() {
  const confirmDialog = useConfirmDialog();
  const [templates, setTemplates] = useState<SystemMessageTemplate[]>([]);
  const [categories, setCategories] = useState<SystemMessageTemplateCategory[]>([]);
  const [activeCategoryId, setActiveCategoryId] = useState<'all' | 'uncategorized' | string>('all');
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [categoryName, setCategoryName] = useState('');
  const [savingCategory, setSavingCategory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState<SystemMessageTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const { toasts, addToast } = useToasts();

  // Form state
  const [formName, setFormName] = useState('');
  const [formCategoryId, setFormCategoryId] = useState<string | null>(null);
  const [formSubject, setFormSubject] = useState('');
  const [formBodyHtml, setFormBodyHtml] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const messageTemplateEditorRef = useRef<ReactQuill | null>(null);
  const messageTemplateSelectionRef = useRef<{ index: number; length: number } | null>(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const [templatesResponse, categoriesResponse] = await Promise.all([
        adminApi.getMessageTemplates(),
        adminApi.getMessageTemplateCategories(),
      ]);
      setTemplates(templatesResponse.data);
      setCategories(categoriesResponse.data);
    } catch (err) {
      console.error('Failed to fetch system message templates:', err);
      addToast('error', 'Nie udało się pobrać szablonów');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const resetForm = () => {
    messageTemplateSelectionRef.current = null;
    setFormName('');
    setFormCategoryId(null);
    setFormSubject('');
    setFormBodyHtml('');
    setFormIsActive(true);
    setEditingTemplate(null);
    setIsCreating(false);
  };

  const openCreateForm = () => {
    resetForm();
    if (activeCategoryId !== 'all' && activeCategoryId !== 'uncategorized') {
      setFormCategoryId(activeCategoryId);
    }
    setIsCreating(true);
  };

  const openEditForm = (template: SystemMessageTemplate) => {
    messageTemplateSelectionRef.current = null;
    setFormName(template.name);
    setFormCategoryId(template.categoryId);
    setFormSubject(template.subject || '');
    setFormBodyHtml(template.bodyHtml);
    setFormIsActive(template.isActive);
    setEditingTemplate(template);
    setIsCreating(false);
  };

  const handleSave = async () => {
    if (!formName || !formBodyHtml) {
      addToast('error', 'Wypełnij nazwę i treść szablonu');
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        const { data } = await adminApi.updateMessageTemplate(editingTemplate.id, {
          name: formName,
          categoryId: formCategoryId,
          subject: formSubject || undefined,
          bodyHtml: formBodyHtml,
          isActive: formIsActive,
        });
        setTemplates(prev => prev.map(t => (t.id === data.id ? data : t)));
        addToast('success', 'Szablon zaktualizowany');
      } else {
        const { data } = await adminApi.createMessageTemplate({
          name: formName,
          categoryId: formCategoryId,
          subject: formSubject || undefined,
          bodyHtml: formBodyHtml,
          isActive: formIsActive,
        });
        setTemplates(prev => [...prev, data]);
        addToast('success', 'Szablon utworzony');
      }
      resetForm();
    } catch (err) {
      console.error('Failed to save message template:', err);
      addToast('error', 'Nie udało się zapisać szablonu');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCategory = async () => {
    const name = categoryName.trim();
    if (!name) {
      addToast('error', 'Podaj nazwę kategorii');
      return;
    }
    setSavingCategory(true);
    try {
      const { data } = await adminApi.createMessageTemplateCategory(name);
      setCategories(prev => [...prev, data]);
      setActiveCategoryId(data.id);
      setCategoryName('');
      setShowCategoryForm(false);
      addToast('success', 'Kategoria systemowa utworzona');
    } catch (err) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      addToast('error', message || 'Nie udało się utworzyć kategorii');
    } finally {
      setSavingCategory(false);
    }
  };

  const handleDeleteCategory = async (category: SystemMessageTemplateCategory) => {
    const confirmed = await confirmDialog({
      title: 'Usuń kategorię systemową',
      message: `Kategoria „${category.name}” zostanie usunięta. Jej szablony pozostaną bez kategorii.`,
      confirmLabel: 'Usuń kategorię',
      danger: true,
    });
    if (!confirmed) return;

    try {
      await adminApi.deleteMessageTemplateCategory(category.id);
      setCategories(prev => prev.filter(item => item.id !== category.id));
      setTemplates(prev => prev.map(template => (
        template.categoryId === category.id ? { ...template, categoryId: null } : template
      )));
      if (activeCategoryId === category.id) setActiveCategoryId('all');
      addToast('success', 'Kategoria usunięta');
    } catch {
      addToast('error', 'Nie udało się usunąć kategorii');
    }
  };

  const visibleTemplates = useMemo(() => {
    if (activeCategoryId === 'all') return templates;
    if (activeCategoryId === 'uncategorized') return templates.filter(template => !template.categoryId);
    return templates.filter(template => template.categoryId === activeCategoryId);
  }, [activeCategoryId, templates]);

  const handleDelete = async (id: string) => {
    if (!(await confirmDialog({
      title: 'Usuń szablon',
      message: 'Czy na pewno chcesz usunąć ten szablon?',
      confirmLabel: 'Usuń',
    }))) return;
    try {
      await adminApi.deleteMessageTemplate(id);
      setTemplates(prev => prev.filter(t => t.id !== id));
      if (editingTemplate?.id === id) resetForm();
      addToast('success', 'Szablon usunięty');
    } catch (err) {
      addToast('error', 'Nie udało się usunąć szablonu');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-accent-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-up">
      <ToastContainer toasts={toasts} />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Szablony szybkich odpowiedzi</h2>
          <p className="text-sm text-text-secondary mt-1">
            Predefiniowane odpowiedzi dostępne w skrzynce odbiorczej podczas pisania wiadomości
          </p>
        </div>
        {!isCreating && !editingTemplate && (
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setShowCategoryForm(true)}
              className="btn-secondary flex items-center gap-1.5 text-sm"
            >
              <Plus className="w-4 h-4" /> Nowa kategoria
            </button>
            <button onClick={openCreateForm} className="btn-primary flex items-center gap-1.5 text-sm">
              <Plus className="w-4 h-4" /> Nowy szablon
            </button>
          </div>
        )}
      </div>

      {showCategoryForm && (
        <div className="card flex flex-wrap items-end gap-2 p-4 animate-fade-in">
          <div className="min-w-56 flex-1">
            <label className="mb-1 block text-xs text-text-secondary">Nazwa nowej kategorii systemowej</label>
            <input
              type="text"
              value={categoryName}
              onChange={event => setCategoryName(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  void handleCreateCategory();
                }
              }}
              autoFocus
              maxLength={80}
              className="input w-full text-sm"
              placeholder="np. Dostawa i tracking"
            />
          </div>
          <button onClick={handleCreateCategory} disabled={savingCategory} className="btn-primary flex items-center gap-1.5 text-sm">
            {savingCategory ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Dodaj kategorię
          </button>
          <button
            onClick={() => {
              setShowCategoryForm(false);
              setCategoryName('');
            }}
            className="btn-ghost text-sm"
          >
            Anuluj
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-1.5" aria-label="Kategorie szablonów systemowych">
        <button
          type="button"
          onClick={() => setActiveCategoryId('all')}
          className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
            activeCategoryId === 'all'
              ? 'border-accent-primary/40 bg-accent-primary/15 text-accent-primary'
              : 'border-border bg-surface-raised/40 text-text-secondary hover:border-accent-primary/30 hover:text-text-primary'
          }`}
        >
          Wszystkie <span className="ml-1 text-text-muted">{templates.length}</span>
        </button>
        {categories.map(category => (
          <div
            key={category.id}
            className={`flex items-center overflow-hidden rounded-full border transition-colors ${
              activeCategoryId === category.id
                ? 'border-accent-primary/40 bg-accent-primary/15 text-accent-primary'
                : 'border-border bg-surface-raised/40 text-text-secondary hover:border-accent-primary/30'
            }`}
          >
            <button
              type="button"
              onClick={() => setActiveCategoryId(category.id)}
              className="py-1.5 pl-3 pr-1 text-xs font-medium"
            >
              {category.name} <span className="ml-1 text-text-muted">{templates.filter(template => template.categoryId === category.id).length}</span>
            </button>
            <button
              type="button"
              onClick={() => handleDeleteCategory(category)}
              className="mr-1 rounded-full p-1 text-text-muted hover:bg-danger/10 hover:text-danger"
              title={`Usuń kategorię ${category.name}`}
              aria-label={`Usuń kategorię ${category.name}`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {templates.some(template => !template.categoryId) && (
          <button
            type="button"
            onClick={() => setActiveCategoryId('uncategorized')}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              activeCategoryId === 'uncategorized'
                ? 'border-accent-primary/40 bg-accent-primary/15 text-accent-primary'
                : 'border-border bg-surface-raised/40 text-text-secondary hover:border-accent-primary/30 hover:text-text-primary'
            }`}
          >
            Bez kategorii <span className="ml-1 text-text-muted">{templates.filter(template => !template.categoryId).length}</span>
          </button>
        )}
      </div>

      {/* Form */}
      {(isCreating || editingTemplate) && (
        <div className="card p-5 space-y-4 animate-fade-in">
          <h3 className="text-sm font-semibold text-text-primary">
            {editingTemplate ? 'Edytuj szablon' : 'Nowy szablon systemowy'}
          </h3>

          <div>
            <label className="text-xs text-text-secondary mb-1 block">Nazwa *</label>
            <input
              type="text"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              className="input w-full text-sm"
              placeholder="np. Potwierdzenie wysyłki"
            />
          </div>

          <div>
            <label className="text-xs text-text-secondary mb-1 block">Kategoria</label>
            <Select
              value={formCategoryId || ''}
              onChange={event => setFormCategoryId(event.target.value || null)}
            >
              <option value="">Bez kategorii</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>{category.name}</option>
              ))}
            </Select>
          </div>

          <div>
            <label className="text-xs text-text-secondary mb-1 block">Temat (opcjonalny)</label>
            <input
              type="text"
              value={formSubject}
              onChange={(e) => setFormSubject(e.target.value)}
              className="input w-full text-sm"
              placeholder="np. Re: Twoje zamówienie"
            />
          </div>

          <div>
            <label className="text-xs text-text-secondary mb-1 block">Treść HTML *</label>
            <div className="bg-surface-raised/50 rounded-xl overflow-hidden border border-border-subtle">
              <ReactQuill
                ref={messageTemplateEditorRef}
                theme="snow"
                value={formBodyHtml}
                onChange={setFormBodyHtml}
                onChangeSelection={(range) => {
                  if (range) messageTemplateSelectionRef.current = range;
                }}
                modules={plainTextPasteModules}
                className="inbox-editor"
                placeholder="Wpisz treść szablonu..."
              />
            </div>
            <MessageTemplateVariablePicker
              onInsert={(code) => {
                const editor = messageTemplateEditorRef.current?.getEditor();
                if (!editor) return;
                const range = messageTemplateSelectionRef.current || editor.getSelection();
                const index = Math.min(range?.index ?? Math.max(0, editor.getLength() - 1), Math.max(0, editor.getLength() - 1));
                const selectionLength = Math.min(range?.length ?? 0, Math.max(0, editor.getLength() - 1 - index));
                if (selectionLength > 0) editor.deleteText(index, selectionLength, 'user');
                editor.insertText(index, code, 'user');
                editor.setSelection(index + code.length, 0, 'silent');
                messageTemplateSelectionRef.current = { index: index + code.length, length: 0 };
                setFormBodyHtml(editor.root.innerHTML);
              }}
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={formIsActive}
              onChange={(e) => setFormIsActive(e.target.checked)}
              className="w-4 h-4 rounded border-border bg-surface-hover text-accent-primary focus:ring-accent-primary"
            />
            <span className="text-sm text-text-primary">Aktywny</span>
          </label>

          <div className="flex gap-2 pt-2">
            <button onClick={handleSave} disabled={saving} className="btn-primary text-sm flex items-center gap-1.5">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {editingTemplate ? 'Zapisz zmiany' : 'Utwórz szablon'}
            </button>
            <button onClick={resetForm} className="btn-ghost text-sm">Anuluj</button>
          </div>
        </div>
      )}

      {/* Templates list */}
      {templates.length === 0 && !isCreating ? (
        <EmptyState
          icon={FileText}
          title="Brak szablonów szybkich odpowiedzi"
          description="Utwórz szablon, który będzie dostępny w skrzynce podczas pisania wiadomości"
          size="sm"
          buttonLabel="Nowy szablon"
          onButtonClick={openCreateForm}
          buttonIcon={Plus}
        />
      ) : (
        <div className="space-y-2">
          {visibleTemplates.length === 0 && !isCreating && !editingTemplate && (
            <div className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-text-muted">
              W tej kategorii nie ma jeszcze szablonów systemowych.
            </div>
          )}
          {visibleTemplates.map(t => (
            <div
              key={t.id}
              className="flex items-center gap-3 p-3 bg-surface-raised/30 rounded-xl border border-border-subtle hover:border-border transition-all"
            >
              <FileText className="w-4 h-4 text-accent-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-text-primary truncate">{t.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-accent-secondary">Systemowy</span>
                  {t.categoryId && (
                    <span className="rounded-full bg-accent-primary/10 px-1.5 py-0.5 text-[10px] text-accent-primary">
                      {categories.find(category => category.id === t.categoryId)?.name || 'Kategoria'}
                    </span>
                  )}
                  {!t.isActive && (
                    <span className="text-[10px] text-warning">Nieaktywny</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => openEditForm(t)}
                  className="btn-ghost p-1.5"
                  title="Edytuj"
                >
                  <Edit3 className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => handleDelete(t.id)}
                  className="btn-ghost p-1.5 text-danger"
                  title="Usuń"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Admin Laboratorium (pipelines + fields sub-tabs) ───────────────────────
function AdminLabContent() {
  const [subTab, setSubTab] = useState<'pipelines' | 'fields' | 'imageStyles'>('pipelines');

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-1 bg-surface-raised/50 p-1 rounded-xl border border-border-subtle w-fit">
        <button
          onClick={() => setSubTab('pipelines')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            subTab === 'pipelines'
              ? 'bg-accent-primary/20 text-accent-primary border border-accent-primary/30'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50'
          }`}
        >
          <FlaskConical className="w-3.5 h-3.5" /> Pipeline
        </button>
        <button
          onClick={() => setSubTab('fields')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            subTab === 'fields'
              ? 'bg-accent-primary/20 text-accent-primary border border-accent-primary/30'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50'
          }`}
        >
          <Table2 className="w-3.5 h-3.5" /> Pola
        </button>
        <button
          onClick={() => setSubTab('imageStyles')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
            subTab === 'imageStyles'
              ? 'bg-accent-primary/20 text-accent-primary border border-accent-primary/30'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50'
          }`}
        >
          <ImageIcon className="w-3.5 h-3.5" /> Style grafik
        </button>
      </div>

      {subTab === 'pipelines' && <AdminLabPipelinesContent />}
      {subTab === 'fields' && <AdminLabFieldsContent />}
      {subTab === 'imageStyles' && <AdminLabProductImageStylesContent />}
    </div>
  );
}

interface ProductImageStyleCardAdmin {
  id: string;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  prompt: string;
  accentColor?: string | null;
  sortOrder?: number;
  isActive?: boolean;
}

function emptyProductImageStyleCard(index: number): ProductImageStyleCardAdmin {
  return {
    id: `style-${Date.now()}-${index}`,
    name: '',
    description: '',
    imageUrl: '',
    prompt: '',
    accentColor: '#38bdf8',
    sortOrder: (index + 1) * 10,
    isActive: true,
  };
}

function AdminLabProductImageStylesContent() {
  const confirmDialog = useConfirmDialog();
  const [cards, setCards] = useState<ProductImageStyleCardAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  const inputClass = 'w-full px-3 py-2 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50';
  const labelClass = 'block text-xs font-medium text-text-secondary mb-1';

  const loadStyles = useCallback(async () => {
    setLoading(true);
    try {
      const res = await labApi.getAdminProductImageStyles();
      setCards((res.data.data?.cards || []) as ProductImageStyleCardAdmin[]);
      setError(null);
    } catch (err) {
      console.error('Failed to load product image styles:', err);
      setError('Nie udało się pobrać kart stylu grafik.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStyles();
  }, [loadStyles]);

  const updateCard = (index: number, patch: Partial<ProductImageStyleCardAdmin>) => {
    setCards(prev => prev.map((card, cardIndex) => cardIndex === index ? { ...card, ...patch } : card));
  };

  const handleAddCard = () => {
    setCards(prev => [...prev, emptyProductImageStyleCard(prev.length)]);
  };

  const handleDeleteCard = async (index: number) => {
    const confirmed = await confirmDialog({
      title: 'Usunąć kartę stylu?',
      message: 'Po zapisie karta zniknie z konfiguratora generowania zdjęć produktowych.',
      confirmLabel: 'Usuń',
      cancelLabel: 'Anuluj',
      danger: true,
    });
    if (!confirmed) return;
    setCards(prev => prev.filter((_, cardIndex) => cardIndex !== index));
  };

  const handleSaveStyles = async () => {
    const normalized = cards.map((card, index) => ({
      ...card,
      id: card.id.trim(),
      name: card.name.trim(),
      description: card.description?.trim() || null,
      imageUrl: card.imageUrl?.trim() || null,
      prompt: card.prompt.trim(),
      accentColor: card.accentColor?.trim() || null,
      sortOrder: card.sortOrder ?? (index + 1) * 10,
      isActive: card.isActive !== false,
    }));
    if (normalized.some(card => !card.id || !card.name || !card.prompt)) {
      setError('Każda karta musi mieć ID, nazwę i prompt stylu.');
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await labApi.saveProductImageStyles({ cards: normalized });
      setCards(normalized);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err) {
      console.error('Failed to save product image styles:', err);
      setError('Nie udało się zapisać kart stylu grafik.');
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-accent-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-surface-raised rounded-xl border border-border p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-text-primary">Style zdjęć produktowych</h3>
            <p className="text-sm text-text-muted mt-1">
              Te karty są wyświetlane użytkownikowi jako obrazkowe radio buttony w konfiguratorze generowania galerii.
            </p>
          </div>
          <button onClick={handleAddCard} className="btn-secondary text-sm flex items-center gap-1.5">
            <Plus className="w-3.5 h-3.5" />
            Dodaj styl
          </button>
        </div>
      </div>

      {error && (
        <div className="px-3 py-2 rounded-lg bg-danger/10 border border-danger/20 text-danger text-sm">
          {error}
        </div>
      )}

      <div className="space-y-4">
        {cards.map((card, index) => (
          <div key={`${card.id}-${index}`} className="bg-surface-raised rounded-xl border border-border overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr]">
              <div className="border-b lg:border-b-0 lg:border-r border-border-subtle bg-surface-base/40">
                <div className="aspect-[16/9] bg-surface-base flex items-center justify-center overflow-hidden">
                  {card.imageUrl ? (
                    <img src={card.imageUrl} alt={card.name || 'Podgląd stylu'} className="w-full h-full object-cover" />
                  ) : (
                    <div
                      className="w-full h-full flex items-center justify-center"
                      style={{ backgroundColor: card.accentColor || '#38bdf8' }}
                    >
                      <ImageIcon className="w-9 h-9 text-white/85" />
                    </div>
                  )}
                </div>
                <div className="p-3 flex items-center justify-between gap-3">
                  <label className="flex items-center gap-2 text-xs text-text-secondary">
                    <input
                      type="checkbox"
                      checked={card.isActive !== false}
                      onChange={event => updateCard(index, { isActive: event.target.checked })}
                      className="rounded border-border"
                    />
                    Aktywny
                  </label>
                  <button
                    type="button"
                    onClick={() => handleDeleteCard(index)}
                    className="p-1.5 text-text-muted hover:text-danger transition-colors"
                    title="Usuń"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              <div className="p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <label>
                    <span className={labelClass}>ID</span>
                    <input
                      value={card.id}
                      onChange={event => updateCard(index, { id: event.target.value })}
                      className={inputClass}
                      placeholder="clean-medical"
                    />
                  </label>
                  <label>
                    <span className={labelClass}>Nazwa</span>
                    <input
                      value={card.name}
                      onChange={event => updateCard(index, { name: event.target.value })}
                      className={inputClass}
                      placeholder="Clean Medical"
                    />
                  </label>
                  <label>
                    <span className={labelClass}>Kolejność</span>
                    <input
                      type="number"
                      value={card.sortOrder ?? 0}
                      onChange={event => updateCard(index, { sortOrder: Number(event.target.value) })}
                      className={inputClass}
                    />
                  </label>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-[1fr_140px] gap-3">
                  <label>
                    <span className={labelClass}>URL obrazka podglądu</span>
                    <input
                      value={card.imageUrl || ''}
                      onChange={event => updateCard(index, { imageUrl: event.target.value })}
                      className={inputClass}
                      placeholder="https://..."
                    />
                  </label>
                  <label>
                    <span className={labelClass}>Kolor awaryjny</span>
                    <input
                      type="color"
                      value={card.accentColor || '#38bdf8'}
                      onChange={event => updateCard(index, { accentColor: event.target.value })}
                      className="w-full h-[38px] bg-surface-hover border border-border rounded-lg p-1"
                    />
                  </label>
                </div>

                <label>
                  <span className={labelClass}>Opis widoczny dla użytkownika</span>
                  <input
                    value={card.description || ''}
                    onChange={event => updateCard(index, { description: event.target.value })}
                    className={inputClass}
                    placeholder="Krótki opis stylu"
                  />
                </label>

                <label>
                  <span className={labelClass}>Prompt stylu</span>
                  <textarea
                    value={card.prompt}
                    onChange={event => updateCard(index, { prompt: event.target.value })}
                    className={`${inputClass} min-h-[140px] resize-y font-mono text-xs leading-relaxed`}
                    placeholder="Instrukcja stylistyczna przekazywana do generatora obrazów"
                  />
                </label>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="sticky bottom-4 z-10 flex items-center justify-between gap-4 bg-surface-raised border border-border rounded-xl p-4 shadow-glass">
        <div className="text-sm">
          {saveStatus === 'success' && <span className="text-success flex items-center gap-1.5"><CheckCircle className="w-4 h-4" /> Zapisano style</span>}
          {saveStatus === 'error' && <span className="text-danger flex items-center gap-1.5"><AlertCircle className="w-4 h-4" /> Błąd zapisu</span>}
          {saveStatus === 'idle' && <span className="text-text-muted">Runtime settings Lab są źródłem prawdy dla tych kart.</span>}
        </div>
        <button onClick={handleSaveStyles} disabled={saving || cards.length === 0} className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Zapisz style
        </button>
      </div>
    </div>
  );
}

function AdminPanelContent() {
  const [templates, setTemplates] = useState<SystemEmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState<SystemEmailTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const { toasts, addToast } = useToasts();

  // Form state
  const [formName, setFormName] = useState('');
  const [formSlug, setFormSlug] = useState('');
  const [formSubject, setFormSubject] = useState('');
  const [formBodyHtml, setFormBodyHtml] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data } = await adminApi.getEmailTemplates();
      setTemplates(data);
    } catch (err) {
      console.error('Failed to fetch system templates:', err);
      addToast('error', 'Nie udało się pobrać szablonów');
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  // ─── Form helpers ────────────────────────────────────────────────────
  const resetForm = () => {
    setFormName('');
    setFormSlug('');
    setFormSubject('');
    setFormBodyHtml('');
    setFormIsActive(true);
    setEditingTemplate(null);
    setIsCreating(false);
  };

  const openCreateForm = () => {
    resetForm();
    setIsCreating(true);
  };

  const openEditForm = (template: SystemEmailTemplate) => {
    setFormName(template.name);
    setFormSlug(template.slug);
    setFormSubject(template.subject);
    setFormBodyHtml(template.bodyHtml);
    setFormIsActive(template.isActive);
    setEditingTemplate(template);
    setIsCreating(false);
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[ąćęłńóśźż]/g, (c) => {
        const map: Record<string, string> = { ą: 'a', ć: 'c', ę: 'e', ł: 'l', ń: 'n', ó: 'o', ś: 's', ź: 'z', ż: 'z' };
        return map[c] || c;
      })
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleNameChange = (name: string) => {
    setFormName(name);
    // Auto-generate slug only when creating
    if (isCreating) {
      setFormSlug(generateSlug(name));
    }
  };

  const handleSave = async () => {
    if (!formName || !formSlug || !formSubject || !formBodyHtml) {
      addToast('error', 'Wypełnij wszystkie wymagane pola');
      return;
    }

    setSaving(true);
    try {
      if (editingTemplate) {
        const { data } = await adminApi.updateEmailTemplate(editingTemplate.id, {
          name: formName,
          slug: formSlug,
          subject: formSubject,
          bodyHtml: formBodyHtml,
          isActive: formIsActive,
        });
        setTemplates(prev => prev.map(t => (t.id === data.id ? data : t)));
        addToast('success', 'Szablon zaktualizowany');
      } else {
        const { data } = await adminApi.createEmailTemplate({
          name: formName,
          slug: formSlug,
          subject: formSubject,
          bodyHtml: formBodyHtml,
          isActive: formIsActive,
        });
        setTemplates(prev => [...prev, data]);
        addToast('success', 'Szablon utworzony');
      }
      resetForm();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd zapisu';
      addToast('error', msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await adminApi.deleteEmailTemplate(id);
      setTemplates(prev => prev.filter(t => t.id !== id));
      setDeleteConfirmId(null);
      if (editingTemplate?.id === id) resetForm();
      addToast('success', 'Szablon usunięty');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd usuwania';
      addToast('error', msg);
    }
  };

  const handleToggleActive = async (template: SystemEmailTemplate) => {
    try {
      const { data } = await adminApi.updateEmailTemplate(template.id, {
        isActive: !template.isActive,
      });
      setTemplates(prev => prev.map(t => (t.id === data.id ? data : t)));
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd zmiany statusu';
      addToast('error', msg);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const { data } = await adminApi.seedEmailTemplates();
      addToast('success', data.message);
      fetchTemplates();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Błąd importu';
      addToast('error', msg);
    }
  };

  // ─── Drag & drop ────────────────────────────────────────────────────
  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = async (targetIndex: number) => {
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newTemplates = [...templates];
    const [moved] = newTemplates.splice(dragIndex, 1);
    newTemplates.splice(targetIndex, 0, moved);
    setTemplates(newTemplates);
    setDragIndex(null);
    setDragOverIndex(null);

    try {
      await adminApi.reorderEmailTemplates(newTemplates.map(t => t.id));
    } catch (err) {
      console.error('Failed to reorder:', err);
      addToast('error', 'Nie udało się zapisać kolejności');
      fetchTemplates(); // revert
    }
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  // ─── Quill config ───────────────────────────────────────────────────
  const quillModules = useMemo(() => withPlainTextPaste({
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ color: [] }, { background: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ align: [] }],
      ['link', 'image'],
      ['clean'],
    ],
  }), []);

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet',
    'align',
    'link', 'image',
  ];

  // ─── Showing form? ─────────────────────────────────────────────────
  const showForm = isCreating || editingTemplate !== null;

  // ─── Render ─────────────────────────────────────────────────────────
  return (
    <div className="space-y-8">
      <ToastContainer toasts={toasts} />

      {/* System Email Templates Section */}
      <div className="card animate-slide-up">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-accent-primary to-accent-secondary">
              <Mail className="w-5 h-5 text-dark-900" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-text-primary">Szablony powiadomień statusowych</h2>
              <p className="text-sm text-text-secondary">
                Szablony emaili wysyłanych automatycznie przy zmianach statusów zamówień
              </p>
            </div>
          </div>
          {!showForm && (
            <div className="flex items-center gap-2">
              {templates.length === 0 && (
                <button
                  onClick={handleSeedDefaults}
                  className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-surface-hover text-text-secondary hover:text-text-primary hover:bg-surface-muted transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Importuj domyślne
                </button>
              )}
              <button
                onClick={openCreateForm}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-accent-primary text-dark-900 hover:bg-accent-primary/90 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Nowy szablon
              </button>
            </div>
          )}
        </div>

        {/* ─── Template List ──────────────────────────────────────── */}
        {!showForm && (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-accent-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : templates.length === 0 ? (
              <EmptyState
                icon={Mail}
                title="Brak systemowych szablonów e-mail"
                description='Kliknij "Importuj domyślne" lub "Nowy szablon" aby zacząć'
                size="sm"
              />
            ) : (
              <div className="space-y-1">
                {templates.map((template, index) => (
                  <div
                    key={template.id}
                    draggable
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={() => handleDrop(index)}
                    onDragEnd={handleDragEnd}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all group ${
                      dragOverIndex === index
                        ? 'bg-accent-primary/10 border border-accent-primary/30'
                        : 'bg-surface-hover/30 hover:bg-surface-hover/50 border border-transparent'
                    } ${dragIndex === index ? 'opacity-50' : ''}`}
                  >
                    <GripVertical className="w-4 h-4 text-text-disabled cursor-grab active:cursor-grabbing flex-shrink-0" />

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-text-primary truncate">
                          {template.name}
                        </span>
                        <span className="text-xs text-text-muted font-mono">
                          {template.slug}
                        </span>
                        {!template.isActive && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-warning/20 text-warning">
                            Nieaktywny
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-text-muted truncate mt-0.5">
                        {template.subject}
                      </p>
                    </div>

                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleToggleActive(template)}
                        className="p-1.5 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-muted transition-colors"
                        title={template.isActive ? 'Dezaktywuj' : 'Aktywuj'}
                      >
                        {template.isActive ? (
                          <ToggleRight className="w-4 h-4 text-accent-primary" />
                        ) : (
                          <ToggleLeft className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => openEditForm(template)}
                        className="p-1.5 rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-muted transition-colors"
                        title="Edytuj"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {deleteConfirmId === template.id ? (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDelete(template.id)}
                            className="px-2 py-1 text-xs rounded bg-danger/20 text-danger hover:bg-danger/30 transition-colors"
                          >
                            Usuń
                          </button>
                          <button
                            onClick={() => setDeleteConfirmId(null)}
                            className="px-2 py-1 text-xs rounded bg-surface-muted text-text-secondary hover:text-text-primary transition-colors"
                          >
                            Anuluj
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => setDeleteConfirmId(template.id)}
                          className="p-1.5 rounded-lg text-text-secondary hover:text-danger hover:bg-danger/10 transition-colors"
                          title="Usuń"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ─── Create / Edit Form ─────────────────────────────────── */}
        {showForm && (
          <div className="space-y-4">
            <button
              onClick={resetForm}
              className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Wróć do listy
            </button>

            <h3 className="text-base font-semibold text-text-primary">
              {editingTemplate ? `Edytuj: ${editingTemplate.name}` : 'Nowy szablon systemowy'}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Nazwa <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className="input"
                  placeholder="np. Zamówienie przyjęte"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-1">
                  Slug <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={formSlug}
                  onChange={(e) => setFormSlug(e.target.value)}
                  className="input font-mono text-sm"
                  placeholder="np. order-received"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Temat wiadomości <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                value={formSubject}
                onChange={(e) => setFormSubject(e.target.value)}
                className="input"
                placeholder="np. Dziękujemy za zamówienie {{numer_zamowienia}}"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">
                Treść HTML <span className="text-danger">*</span>
              </label>
              <div className="bg-white rounded-lg email-editor">
                <ReactQuill
                  value={formBodyHtml}
                  onChange={setFormBodyHtml}
                  modules={quillModules}
                  formats={quillFormats}
                  theme="snow"
                  placeholder="Treść wiadomości e-mail..."
                />
              </div>
              <p className="text-xs text-text-muted mt-1.5">
                Dostępne zmienne: {'{{imie}}'}, {'{{nazwisko}}'}, {'{{numer_zamowienia}}'}, {'{{produkty}}'}, {'{{wartosc}}'}, {'{{tracking}}'}, {'{{link_sledzenia}}'}, {'{{sklep}}'}, {'{{sklep_email}}'}, {'{{sklep_telefon}}'}, {'{{konto_bankowe}}'}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setFormIsActive(!formIsActive)}
                className="flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                {formIsActive ? (
                  <ToggleRight className="w-5 h-5 text-accent-primary" />
                ) : (
                  <ToggleLeft className="w-5 h-5 text-text-muted" />
                )}
                {formIsActive ? 'Aktywny' : 'Nieaktywny'}
              </button>
            </div>

            <div className="flex items-center gap-3 pt-2 border-t border-border-subtle">
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-accent-primary text-dark-900 hover:bg-accent-primary/90 disabled:opacity-50 transition-colors"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Zapisywanie...' : (editingTemplate ? 'Zapisz zmiany' : 'Utwórz szablon')}
              </button>
              <button
                onClick={resetForm}
                className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg text-text-secondary hover:text-text-primary hover:bg-surface-hover transition-colors"
              >
                <X className="w-4 h-4" />
                Anuluj
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Lab Pipeline Config (Admin) ─────────────────────────────────────────────

interface PipelineItem {
  type: 'instruction' | 'field' | 'choice' | 'user_input' | 'image_gen';
  name: string;
  title?: string;
  group?: string | null;
  displayGroup?: string | null;
  displayOrder?: number | null;
  icon?: string;
  prompt: string;
  contextFrom?: string[] | null;
  outputFormat?: string;
  choiceCount?: number;
  optionsFromField?: string | null;
  applyChoiceToField?: string | null;
  aiShortcode?: string;
  ai_shortcode?: string;
  // user_input fields
  inputType?: 'text' | 'textarea' | 'link' | 'file' | 'packages' | 'select';
  label?: string;
  helperText?: string;
  required?: boolean;
  placeholder?: string;
  options?: Array<string | { value: string; label: string; description?: string }>;
  defaultValue?: string;
  visibleWhen?: Array<{
    field: string;
    equals?: string | string[];
    notEquals?: string | string[];
  }>;
  // per-step wizard config (for user_input)
  panelTitle?: string;
  panelDescription?: string;
  ctaLabel?: string;
  backLabel?: string;
  allowMultiple?: boolean;
  autoFillFrom?: string;
  screenKey?: string;
  fileAccept?: string;
  // Per-step AI config overrides
  deepResearch?: boolean;        // legacy — mapped to researchLevel 'search'
  researchLevel?: 'off' | 'search' | 'deep_research';
  modelProvider?: string;        // override: 'claude' | 'gemini' | 'chatgpt' | '' (default)
  modelId?: string;              // override: specific model ID | '' (default)
  temperature?: number;          // override: 0-2 (default from provider settings)
  progressMessages?: string[];
  promptSourceField?: string | null;
  promptSourceSlot?: string | null;
  promptSourceParagraph?: number | null;
  runtimePack?: string | null;
  unpackJsonOutputs?: boolean;
  jsonOutputFields?: string[];
  technicalOutput?: 'ad_copy_preview' | 'ad_copy_runtime_plan';
  hidden?: boolean;
  imageSize?: '1024x1024' | '1024x1792' | '1792x1024';
  imageStyle?: 'natural' | 'vivid';
  imageProvider?: string;
  imageCount?: number;
}

type LabPipelineTab = 'KNOWLEDGE' | 'REFERENCE_IMAGES' | 'PRODUCT_IMAGES' | 'LANDING' | 'ADVERTORIAL' | 'LISTICALE' | 'QUIZ' | 'EXPLAINER' | 'VSL' | 'VSL_STORY' | 'HOOKS' | 'VIDEO_SCENES' | 'VIDEO_BROLL' | 'VIDEO_EDITOR_BRIEF' | 'VIDEO_PARAPHRASE' | 'VIDEO_REHOOK' | 'STATIC_PROPOSE' | 'STATIC_SCHEMAS' | 'STATIC_FUNNEL' | 'STATIC_CAROUSEL' | 'STATIC_WINNER' | 'STATIC_COMPETITOR' | 'SOCIAL_PLAN' | 'AD_COPY' | 'UGC_SCRIPT' | 'SOCIAL_POST';
type BuilderPageType = 'ADVERTORIAL' | 'STANDALONE' | 'PRODUCT_PAGE' | 'QUIZ';
const LAB_PIPELINE_TAB_PARAM = 'labPipeline';
const LAB_PIPELINE_TAB_STORAGE_KEY = 'ecomflow.admin.lab.pipelineTab';

interface BuilderPageConfig {
  [key: string]: unknown;
  pageType: BuilderPageType;
  templateId?: string | null;
  templateName?: string | null;
  templateContent?: unknown[] | null;
  templateUploadedAt?: string | null;
  productTitleTemplate?: string | null;
  productGalleryShortcodes?: string[] | null;
  productShortDescriptionTemplate?: string | null;
  productShortDescriptionBuilder?: ProductShortDescriptionElement[] | null;
  productAdditionalElements?: ProductAdditionalElement[] | null;
}

const PIPELINE_LABELS: Record<LabPipelineTab, string> = {
  KNOWLEDGE: 'Baza wiedzy',
  REFERENCE_IMAGES: 'Zdjęcia referencyjne',
  PRODUCT_IMAGES: 'Zdjęcia produktowe',
  ADVERTORIAL: 'Advertoriale',
  LISTICALE: 'Listical',
  QUIZ: 'Quizy',
  LANDING: 'Produktowe',
  EXPLAINER: 'Explainer',
  VSL: 'VSL',
  VSL_STORY: 'VSL Story',
  HOOKS: 'Hooki',
  VIDEO_SCENES: 'Wideo: sceny',
  VIDEO_BROLL: 'Wideo: b-rolle',
  VIDEO_EDITOR_BRIEF: 'Wideo: brief montażu',
  VIDEO_PARAPHRASE: 'Wideo: parafraza',
  VIDEO_REHOOK: 'Wideo: nowy hook',
  STATIC_PROPOSE: 'Statyczne: AI proponuje',
  STATIC_SCHEMAS: 'Statyczne: schematy',
  STATIC_FUNNEL: 'Statyczne: pakiet lejka',
  STATIC_CAROUSEL: 'Statyczne: karuzela',
  STATIC_WINNER: 'Statyczne: iteracje winnera',
  STATIC_COMPETITOR: 'Statyczne: z konkurencji',
  SOCIAL_PLAN: 'Social: harmonogram',
  AD_COPY: 'Ad Copy',
  UGC_SCRIPT: 'Skrypty UGC',
  SOCIAL_POST: 'Social Media',
};

function isLabPipelineTab(value: string | null | undefined): value is LabPipelineTab {
  return Boolean(value && Object.prototype.hasOwnProperty.call(PIPELINE_LABELS, value));
}

function storedLabPipelineTab(): LabPipelineTab | null {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(LAB_PIPELINE_TAB_STORAGE_KEY);
  return isLabPipelineTab(stored) ? stored : null;
}

const INPUT_TYPE_LABELS: Record<string, string> = {
  text: 'Krótki tekst',
  textarea: 'Długi tekst',
  link: 'Link URL',
  file: 'Załącznik',
  packages: 'Pakiety cenowe',
  select: 'Lista wyboru',
};

interface AutoFillOption {
  value: string;
  label: string;
}

const BASE_AUTO_FILL_OPTIONS: AutoFillOption[] = [
  { value: '', label: '— brak —' },
  { value: 'name', label: 'Nazwa produktu' },
  { value: 'description', label: 'Opis produktu' },
  { value: 'niche', label: 'Nisza' },
  { value: 'adsLibrary', label: 'Biblioteka reklam' },
  { value: 'advertorial', label: 'Prelanding' },
  { value: 'landing', label: 'Landing' },
  { value: 'marketplaceLink', label: 'Aukcje marketplace' },
  { value: 'adTranscripts', label: 'Transkrypty reklam' },
  { value: 'reviewsCsv', label: 'Komentarze użytkowników' },
  { value: 'competitorLinks', label: 'Linki konkurencji' },
  { value: 'otherSources', label: 'Inne źródła' },
  { value: 'scrapedContent', label: 'Scrapowane treści' },
  { value: 'pricePackages', label: 'Pakiety cenowe' },
];

function buildAutoFillOptions(fields: Array<{ key?: string; label?: string }>): AutoFillOption[] {
  const byValue = new Map(BASE_AUTO_FILL_OPTIONS.map(option => [option.value, option]));
  for (const field of fields) {
    if (!field.key || !field.label) continue;
    byValue.set(field.key, { value: field.key, label: field.label });
  }
  return Array.from(byValue.values());
}

const BUILDER_PAGE_TYPE_LABELS: Record<BuilderPageType, string> = {
  ADVERTORIAL: 'Advertorial',
  STANDALONE: 'Strona',
  PRODUCT_PAGE: 'Strona produktowa',
  QUIZ: 'Quiz',
};

const KNOWLEDGE_PIPELINE_GROUPS = ['Produkt', 'Persona', 'Konkurencja', 'Finanse'];
const KNOWLEDGE_INPUT_GROUP = 'Materiały wejściowe';

function defaultBuilderPageForPipeline(pipeline: LabPipelineTab): BuilderPageConfig | null {
  switch (pipeline) {
    case 'ADVERTORIAL':
      return { pageType: 'ADVERTORIAL', templateId: null };
    case 'LANDING':
      return {
        pageType: 'PRODUCT_PAGE',
        templateId: null,
        productTitleTemplate: '[product_title]',
        productGalleryShortcodes: [
          '[lab_product_image_hero_image]',
          '[lab_product_image_before_after_state]',
          '[lab_product_image_before_after_closeup]',
          '[lab_product_image_hero_product]',
          '[lab_product_image_benefits_grid]',
          '[lab_product_image_universality_grid]',
          '[lab_product_image_mechanism_visual]',
          '[lab_product_image_us_vs_cheap]',
          '[lab_product_image_comparison_table]',
          '[lab_product_image_expert_recommendation]',
          '[lab_product_image_social_proof]',
          '[lab_product_image_how_to_use]',
          '[lab_product_image_paragraph_visual]',
          '[lab_product_image_paragraph_visual_2]',
          '[lab_product_image_customer_review_1]',
          '[lab_product_image_customer_review_2]',
          '[lab_product_image_customer_review_3]',
          '[lab_product_image_customer_review_4]',
          '[lab_product_image_customer_review_5]',
          '[lab_product_image_customer_review_6]',
          '[lab_product_image_customer_review_7]',
          '[lab_product_image_customer_review_8]',
          '[lab_product_image_customer_review_9]',
          '[lab_product_image_customer_review_10]',
          '[lab_product_image_customer_review_11]',
          '[lab_product_image_customer_review_12]',
        ],
      };
    case 'QUIZ':
      return { pageType: 'QUIZ', templateId: null };
    case 'LISTICALE':
      return { pageType: 'STANDALONE', templateId: null };
    default:
      return null;
  }
}

function extractBuilderTemplateContent(value: unknown): unknown[] | null {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    if (Array.isArray(record.content)) return record.content;
    if (Array.isArray(record.blocks)) return record.blocks;
    if (Array.isArray(record.nodes)) return record.nodes;
  }
  return null;
}

// Model info for pipeline step editor — cost + best use case
const STEP_MODELS: Record<string, Array<{ id: string; label: string; tier: string; cost: string; bestFor: string }>> = {
  gemini: [
    { id: 'gemini-3.1-flash-lite-preview', label: 'Gemini 3.1 Flash Lite', tier: 'fast', cost: '$0.25/$1.50', bestFor: 'Proste pola, tagi, bulk' },
    { id: 'gemini-3.1-pro-preview', label: 'Gemini 3.1 Pro', tier: 'premium', cost: '~$2.50/$15', bestFor: 'Deep analysis, strategia' },
    { id: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', tier: 'fast', cost: '$0.15/$0.60', bestFor: 'KB sekcje, szybkie opisy' },
    { id: 'gemini-2.5-flash-lite', label: 'Gemini 2.5 Flash Lite', tier: 'fast', cost: '$0.08/$0.30', bestFor: 'Ultrabudżet — formatowanie' },
    { id: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro', tier: 'balanced', cost: '$1.25/$10', bestFor: 'Advertoriale, landing' },
    { id: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash (dep.)', tier: 'fast', cost: '$0.10/$0.40', bestFor: 'Legacy' },
  ],
  chatgpt: [
    ...CHATGPT_TEXT_MODELS,
  ],
  kimi: KIMI_TEXT_MODELS,
  claude: [
    { id: 'claude-haiku-4-5-20251001', label: 'Haiku 4.5', tier: 'fast', cost: '$1/$5', bestFor: 'Klasyfikacja, tagi, krótkie pola' },
    { id: 'claude-sonnet-4-6', label: 'Sonnet 4.6', tier: 'balanced', cost: '$3/$15', bestFor: 'Opisy, ad copy, landing' },
    { id: 'claude-opus-4-6', label: 'Opus 4.6', tier: 'premium', cost: '$5/$25', bestFor: 'Złożone analizy, strategia, USP' },
  ],
};

const STEP_TIER_INFO: Record<string, { color: string; bg: string; label: string; icon: string }> = {
  fast: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', label: 'Fast', icon: '$' },
  balanced: { color: 'text-accent-info', bg: 'bg-info/10', label: 'Balanced', icon: '$$' },
  premium: { color: 'text-amber-400', bg: 'bg-amber-500/10', label: 'Premium', icon: '$$$' },
  reasoning: { color: 'text-purple-400', bg: 'bg-purple-500/10', label: 'Reasoning', icon: '$$' },
};

function getItemBorderClass(type: string): string {
  switch (type) {
    case 'instruction': return 'border-l-yellow-500/50 border border-l-yellow-500/50 border-border';
    case 'choice': return 'border-l-amber-500/50 border border-l-amber-500/50 border-border';
    case 'user_input': return 'border-l-sky-500/50 border border-l-sky-500/50 border-border';
    case 'image_gen': return 'border-l-fuchsia-500/50 border border-l-fuchsia-500/50 border-border';
    default: return 'border-l-emerald-500/50 border border-l-emerald-500/50 border-border';
  }
}

function getItemBadge(type: string): { bg: string; text: string; label: string } {
  switch (type) {
    case 'instruction': return { bg: 'bg-warning/20', text: 'text-warning', label: 'Instrukcja' };
    case 'choice': return { bg: 'bg-amber-500/20', text: 'text-amber-400', label: 'Wybór' };
    case 'user_input': return { bg: 'bg-sky-500/20', text: 'text-sky-400', label: 'Dane wejściowe' };
    case 'image_gen': return { bg: 'bg-fuchsia-500/20', text: 'text-fuchsia-400', label: 'Obraz' };
    default: return { bg: 'bg-emerald-500/20', text: 'text-emerald-400', label: 'Pole' };
  }
}

function getPipelineAiShortcode(item: PipelineItem): string {
  return item.aiShortcode || item.ai_shortcode || '';
}

function SortablePipelineItem({
  item,
  index,
  onEdit,
  onDelete,
}: {
  item: PipelineItem;
  index: number;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const sortableId = `pipeline-${index}`;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortableId });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const badge = getItemBadge(item.type);

  return (
    <div ref={setNodeRef} style={style}>
      <div className={`flex items-center gap-3 p-3 bg-surface-raised rounded-lg border-l-4 ${getItemBorderClass(item.type)}`}>
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-text-disabled hover:text-text-secondary">
          <GripVertical className="w-4 h-4" />
        </button>
        <span className={`text-xs px-1.5 py-0.5 rounded ${badge.bg} ${badge.text}`}>
          {badge.label}
        </span>
        {item.icon && <span>{item.icon}</span>}
        <div className="flex-1">
          <div className="text-sm text-text-primary flex items-center gap-1.5">
            {item.label || item.title || item.name}
            {(item.researchLevel === 'deep_research') && (
              <span className="px-1.5 py-0.5 text-[9px] font-medium bg-amber-500/15 text-amber-400 rounded">Deep</span>
            )}
            {(item.researchLevel === 'search' || (!item.researchLevel && item.deepResearch)) && (
              <span className="px-1.5 py-0.5 text-[9px] font-medium bg-emerald-500/15 text-emerald-400 rounded">Search</span>
            )}
            {item.modelProvider && (
              <span className="px-1.5 py-0.5 text-[9px] font-medium bg-info/10 text-accent-info rounded">
                {item.modelId || item.modelProvider}
              </span>
            )}
            {item.temperature != null && (
              <span className="px-1.5 py-0.5 text-[9px] font-medium bg-purple-500/10 text-purple-400 rounded">
                t={item.temperature}
              </span>
            )}
            {getPipelineAiShortcode(item) && (
              <span className="px-1.5 py-0.5 text-[9px] font-medium bg-accent-primary/10 text-accent-primary rounded">
                [{getPipelineAiShortcode(item)}]
              </span>
            )}
            {item.group && (
              <span className="px-1.5 py-0.5 text-[9px] font-medium bg-surface-hover text-text-secondary rounded">
                {item.group}
              </span>
            )}
          </div>
          {item.type === 'user_input' ? (
            <div className="text-xs text-text-muted truncate max-w-md">
              {INPUT_TYPE_LABELS[item.inputType || 'text']}
              {item.required && ' (wymagane)'}
              {item.helperText && ` — ${item.helperText.substring(0, 60)}`}
            </div>
          ) : item.prompt ? (
            <div className="text-xs text-text-muted truncate max-w-md">{item.prompt.substring(0, 80)}...</div>
          ) : null}
        </div>
        <button onClick={onEdit} className="px-3 py-1 text-xs text-text-secondary hover:text-text-primary bg-surface-hover rounded">
          Edytuj
        </button>
        <button onClick={onDelete} className="p-1.5 text-text-muted hover:text-danger">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function AdminLabPipelinesContent() {
  const confirmDialog = useConfirmDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPipelineTab = searchParams.get(LAB_PIPELINE_TAB_PARAM);
  const [activeTab, setActiveTabState] = useState<LabPipelineTab>(() => (
    isLabPipelineTab(urlPipelineTab) ? urlPipelineTab : storedLabPipelineTab() ?? 'KNOWLEDGE'
  ));
  const [pipelineItems, setPipelineItems] = useState<PipelineItem[]>([]);
  const [builderPage, setBuilderPage] = useState<BuilderPageConfig | null>(null);
  const [savingPipeline, setSavingPipeline] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [editingItem, setEditingItem] = useState<PipelineItem | null>(null);
  const [editingIndex, setEditingIndex] = useState<number>(-1);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [autoFillOptions, setAutoFillOptions] = useState<AutoFillOption[]>(BASE_AUTO_FILL_OPTIONS);

  // Close edit modal on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && editingItem) {
        setEditingItem(null);
        setEditingIndex(-1);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [editingItem]);

  useEffect(() => {
    loadPipeline(activeTab);
  }, [activeTab]);

  useEffect(() => {
    const nextTab = isLabPipelineTab(urlPipelineTab) ? urlPipelineTab : null;
    if (nextTab && nextTab !== activeTab) {
      setActiveTabState(nextTab);
    }
  }, [activeTab, urlPipelineTab]);

  useEffect(() => {
    window.localStorage.setItem(LAB_PIPELINE_TAB_STORAGE_KEY, activeTab);
  }, [activeTab]);

  const setActiveTab = useCallback((tab: LabPipelineTab) => {
    setActiveTabState(tab);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(LAB_PIPELINE_TAB_PARAM, tab);
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    labApi.getFieldConfig()
      .then(res => {
        const fields = (res.data.data?.fields || []) as Array<{ key?: string; label?: string }>;
        setAutoFillOptions(buildAutoFillOptions(fields));
      })
      .catch(err => {
        console.error('Failed to load lab field config for autofill mapping:', err);
        setAutoFillOptions(BASE_AUTO_FILL_OPTIONS);
      });
  }, []);

  const loadPipeline = async (pipeline: string) => {
    setPipelineLoading(true);
    try {
      const res = await labApi.getAdminPipeline(pipeline);
      const config = res.data.data;
      const items = config?.items || [];
      const loadedBuilderPage = config?.builderPage || defaultBuilderPageForPipeline(pipeline as LabPipelineTab);
      const normalizedBuilderPage = loadedBuilderPage
        ? {
            ...loadedBuilderPage,
            productTitleTemplate: loadedBuilderPage.pageType === 'PRODUCT_PAGE'
              ? loadedBuilderPage.productTitleTemplate || '[product_title]'
              : loadedBuilderPage.productTitleTemplate || null,
            productGalleryShortcodes: Array.isArray(loadedBuilderPage.productGalleryShortcodes)
              ? loadedBuilderPage.productGalleryShortcodes.filter((item: unknown): item is string => typeof item === 'string')
              : defaultBuilderPageForPipeline(pipeline as LabPipelineTab)?.productGalleryShortcodes || null,
            productShortDescriptionBuilder: normalizeProductShortDescriptionElements(loadedBuilderPage.productShortDescriptionBuilder),
            productAdditionalElements: normalizeProductAdditionalElements(loadedBuilderPage.productAdditionalElements),
          }
        : null;
      // Migrate legacy global config into user_input items that don't have per-step config
      const migratedItems = (items as PipelineItem[]).map(item => {
        if (item.type === 'user_input') {
          return {
            ...item,
            panelTitle: item.panelTitle || config?.inputPanelTitle || '',
            panelDescription: item.panelDescription || config?.inputPanelDescription || '',
            ctaLabel: item.ctaLabel || config?.ctaLabel || '',
            backLabel: item.backLabel || config?.backLabel || '',
          };
        }
        return item;
      });
      setPipelineItems(migratedItems);
      setBuilderPage(normalizedBuilderPage);
    } catch {
      setPipelineItems([]);
      setBuilderPage(defaultBuilderPageForPipeline(pipeline as LabPipelineTab));
    } finally {
      setPipelineLoading(false);
    }
  };

  const handleAddBlock = (type: PipelineItem['type']) => {
    const newItem: PipelineItem = {
      type,
      name: `${type}_${Date.now()}`,
      title: type !== 'instruction' ? '' : '',
      icon: type === 'image_gen' ? '🖼️' : type !== 'instruction' && type !== 'user_input' ? '📋' : '',
      group: activeTab === 'KNOWLEDGE'
        ? type === 'user_input'
          ? KNOWLEDGE_INPUT_GROUP
          : type === 'instruction'
            ? null
            : KNOWLEDGE_PIPELINE_GROUPS[0]
        : null,
      prompt: type === 'user_input' ? '' : '',
      contextFrom: null,
      outputFormat: 'freetext',
      ...(type === 'choice' ? { choiceCount: 5, optionsFromField: '', applyChoiceToField: '' } : {}),
      ...(type === 'user_input' ? { inputType: 'text' as const, label: '', helperText: '', required: false, placeholder: '', defaultValue: '', options: [], visibleWhen: [], panelTitle: '', panelDescription: '', ctaLabel: '', backLabel: '', allowMultiple: false, autoFillFrom: '', screenKey: '', fileAccept: '' } : {}),
      ...(type === 'image_gen' ? { imageSize: '1024x1024' as const, imageStyle: 'natural' as const, imageCount: 1 } : {}),
    };
    setEditingItem(newItem);
    setEditingIndex(-1);
  };

  const handleEditBlock = (index: number) => {
    setEditingItem({ ...pipelineItems[index] });
    setEditingIndex(index);
  };

  const handleSaveBlock = () => {
    if (!editingItem) return;
    setPipelineItems(prev => {
      if (editingIndex === -1) return [...prev, editingItem];
      const next = [...prev];
      next[editingIndex] = editingItem;
      return next;
    });
    setEditingItem(null);
    setEditingIndex(-1);
  };

  const handleDeleteBlock = (index: number) => {
    setPipelineItems(prev => prev.filter((_, i) => i !== index));
  };

  const pipelineSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handlePipelineDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setPipelineItems(prev => {
      const oldIndex = prev.findIndex((_, i) => `pipeline-${i}` === active.id);
      const newIndex = prev.findIndex((_, i) => `pipeline-${i}` === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleSavePipeline = async () => {
    setSavingPipeline(true);
    try {
      await labApi.savePipeline(activeTab, {
        items: pipelineItems,
        builderPage: activeTab === 'KNOWLEDGE' || activeTab === 'REFERENCE_IMAGES' || activeTab === 'PRODUCT_IMAGES'
          ? null
          : builderPage
            ? {
                ...builderPage,
                pageType: builderPage.pageType,
                templateId: builderPage.templateId || null,
                templateName: builderPage.templateName || null,
                templateContent: builderPage.templateContent || null,
                templateUploadedAt: builderPage.templateUploadedAt || null,
                productTitleTemplate: builderPage.productTitleTemplate || null,
                productGalleryShortcodes: builderPage.productGalleryShortcodes || null,
                productShortDescriptionTemplate: builderPage.productShortDescriptionTemplate || null,
                productShortDescriptionBuilder: builderPage.productShortDescriptionBuilder || null,
                productAdditionalElements: builderPage.productAdditionalElements || null,
              }
            : null,
      });
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err) {
      console.error('Error saving pipeline:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setSavingPipeline(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50';
  const labelClass = 'block text-sm font-medium text-text-secondary mb-1';
  const showBuilderPageConfig = activeTab !== 'KNOWLEDGE' && activeTab !== 'REFERENCE_IMAGES' && activeTab !== 'PRODUCT_IMAGES';

  const handleBuilderTemplateUpload = async (file: File) => {
    const raw = await file.text();
    const parsed = JSON.parse(raw) as unknown;
    const content = extractBuilderTemplateContent(parsed);
    if (!content || content.length === 0) {
      throw new Error('JSON nie zawiera tablicy content, blocks ani nodes.');
    }
    setBuilderPage(prev => ({
      ...(prev || { pageType: 'STANDALONE' as const }),
      templateId: null,
      templateName: file.name,
      templateContent: content,
      templateUploadedAt: new Date().toISOString(),
    }));
  };

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="flex gap-1 bg-surface-raised rounded-lg p-1 border border-border overflow-x-auto max-w-full">
        {(Object.keys(PIPELINE_LABELS) as LabPipelineTab[]).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-3 py-2 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
              activeTab === tab ? 'bg-surface-hover text-text-primary' : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {PIPELINE_LABELS[tab]}
          </button>
        ))}
      </div>

      {showBuilderPageConfig && (
        <div className="bg-surface-raised rounded-xl border border-border p-4 space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-text-primary flex items-center gap-2">
                <FileText className="w-4 h-4 text-accent-primary" />
                Output Kreatora
              </h3>
              <p className="text-xs text-text-muted mt-1">
                To ustawienie decyduje, jaki typ strony i który dedykowany szablon zostaną użyte przy generowaniu strony z tego pipeline.
              </p>
            </div>
            <label className="flex items-center gap-2 text-xs text-text-secondary cursor-pointer shrink-0">
              <input
                type="checkbox"
                checked={Boolean(builderPage)}
                onChange={e => setBuilderPage(e.target.checked ? (builderPage || defaultBuilderPageForPipeline(activeTab) || { pageType: 'STANDALONE', templateId: null }) : null)}
                className="rounded border-border-strong bg-surface-hover text-accent-primary focus:ring-accent-primary/30"
              />
              Generuje stronę
            </label>
          </div>

          {builderPage ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Typ strony</label>
                  <select
                    value={builderPage.pageType}
                    onChange={e => setBuilderPage(prev => ({ ...(prev || { templateId: null }), pageType: e.target.value as BuilderPageType }))}
                    className={inputClass}
                  >
                    {Object.entries(BUILDER_PAGE_TYPE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Techniczny szablon JSON</label>
                  <label className="flex items-center justify-center gap-2 px-3 py-2 bg-surface-hover border border-dashed border-border rounded-lg text-sm text-text-secondary hover:text-text-primary hover:border-accent-primary/40 cursor-pointer transition-colors">
                    <FileUp className="w-4 h-4" />
                    Wgraj szablon .json
                    <input
                      type="file"
                      accept="application/json,.json"
                      className="hidden"
                      onChange={async e => {
                        const file = e.target.files?.[0];
                        e.currentTarget.value = '';
                        if (!file) return;
                        try {
                          await handleBuilderTemplateUpload(file);
                          setSaveStatus('idle');
                        } catch (err) {
                          console.error('Failed to import lab builder template:', err);
                          setSaveStatus('error');
                          setTimeout(() => setSaveStatus('idle'), 3000);
                        }
                      }}
                    />
                  </label>
                  {builderPage.templateContent?.length ? (
                    <div className="mt-2 flex items-center justify-between gap-3 rounded-lg border border-success/20 bg-success/10 px-3 py-2">
                      <div className="min-w-0">
                        <p className="truncate text-xs font-medium text-success">{builderPage.templateName || 'Wgrany szablon JSON'}</p>
                        <p className="text-[10px] text-success/80">{builderPage.templateContent.length} bloków w szablonie technicznym</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setBuilderPage(prev => prev ? { ...prev, templateName: null, templateContent: null, templateUploadedAt: null } : prev)}
                        className="p-1 text-success/80 hover:text-danger transition-colors"
                        title="Usuń wgrany szablon"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <p className="text-[10px] text-warning mt-1">
                      Wgraj prywatny JSON z technicznymi shortcode’ami. Nie będzie widoczny w bibliotece Szablony Buildera.
                    </p>
                  )}
                </div>
              </div>

              {builderPage.pageType === 'PRODUCT_PAGE' && (
                <div className="space-y-3">
                  <div>
                    <label className={labelClass}>Techniczny tytuł produktu</label>
                    <input
                      type="text"
                      value={builderPage.productTitleTemplate || ''}
                      onChange={e => setBuilderPage(prev => ({ ...(prev || { pageType: 'PRODUCT_PAGE' as const }), productTitleTemplate: e.target.value }))}
                      className={`${inputClass} font-mono text-xs`}
                      placeholder="[product_title]"
                    />
                    <p className="text-[10px] text-text-muted mt-1">
                      Ta wartość zostanie zapisana jako nazwa produktu WooCommerce. Może zawierać shortcode’y z pipeline.
                    </p>
                  </div>

                  <div>
                    <label className={labelClass}>Kolejność galerii produktowej</label>
                    <textarea
                      value={(builderPage.productGalleryShortcodes || []).join('\n')}
                      onChange={e => setBuilderPage(prev => ({
                        ...(prev || { pageType: 'PRODUCT_PAGE' as const }),
                        productGalleryShortcodes: e.target.value
                          .split('\n')
                          .map(line => line.trim())
                          .filter(Boolean),
                      }))}
                      rows={7}
                      className={`${inputClass} resize-y font-mono text-xs`}
                      placeholder="[lab_product_image_hero_image]"
                    />
                    <p className="text-[10px] text-text-muted mt-1">
                      Wklej shortcode’y grafik jedna pozycja na linię. Backend zbuduje shortcode <span className="font-mono text-accent-primary">[lab_product_gallery]</span> dokładnie w tej kolejności, a brakujące gotowe grafiki dopnie na końcu.
                    </p>
                  </div>

                  <ProductShortDescriptionBuilderSection
                    integrationId=""
                    mediaPickerEnabled={false}
                    description="Techniczny wzorzec krótkiego opisu produktu. Elementy mogą zawierać shortcode’y z pipeline i zostaną zapisane jako meta mini-kreatora produktu."
                    value={builderPage.productShortDescriptionBuilder || []}
                    onChange={productShortDescriptionBuilder => setBuilderPage(prev => ({
                      ...(prev || { pageType: 'PRODUCT_PAGE' as const }),
                      productShortDescriptionBuilder,
                    }))}
                  />

                  <ProductAdditionalElementsSection
                    integrationId=""
                    mediaPickerEnabled={false}
                    description="Techniczny wzorzec elementów pod CTA. Pola mogą zawierać shortcode’y z pipeline i zostaną zapisane jako meta produktu."
                    value={builderPage.productAdditionalElements || []}
                    onChange={productAdditionalElements => setBuilderPage(prev => ({
                      ...(prev || { pageType: 'PRODUCT_PAGE' as const }),
                      productAdditionalElements,
                    }))}
                  />

                  <details className="rounded-xl border border-border-subtle bg-surface-base/40 p-3">
                    <summary className="cursor-pointer text-xs font-medium text-text-secondary">
                      Awaryjny HTML krótkiego opisu
                    </summary>
                    <div className="mt-3">
                      <textarea
                        value={builderPage.productShortDescriptionTemplate || ''}
                        onChange={e => setBuilderPage(prev => ({ ...(prev || { pageType: 'PRODUCT_PAGE' as const }), productShortDescriptionTemplate: e.target.value }))}
                        className={`${inputClass} min-h-[120px] font-mono text-xs`}
                        placeholder='<div class="...">[short_product_description]</div>'
                      />
                      <p className="text-[10px] text-text-muted mt-1">
                        Używane tylko wtedy, gdy kreator krótkiego opisu powyżej nie ma żadnych elementów.
                      </p>
                    </div>
                  </details>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-border-subtle bg-surface-hover/40 px-3 py-2 text-xs text-text-muted">
              Ten pipeline nie będzie generował strony w Kreatorze.
            </div>
          )}
        </div>
      )}

      {activeTab === 'KNOWLEDGE' && (
        <div className="bg-surface-raised rounded-xl border border-border p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-text-primary mr-1">Podkategorie Bazy wiedzy</span>
            {KNOWLEDGE_PIPELINE_GROUPS.map(group => {
              const count = pipelineItems.filter(item => item.group === group).length;
              return (
                <span key={group} className="px-2 py-1 rounded-lg bg-surface-hover text-xs text-text-secondary border border-border-subtle">
                  {group}: {count}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Pipeline items */}
      {pipelineLoading ? (
        <div className="text-center text-text-muted py-12">Ładowanie konfiguracji pipeline...</div>
      ) : (
        <div className="space-y-4">
          <DndContext sensors={pipelineSensors} collisionDetection={closestCenter} onDragEnd={handlePipelineDragEnd}>
            <SortableContext items={pipelineItems.map((_, i) => `pipeline-${i}`)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {pipelineItems.map((item, index) => (
                  <SortablePipelineItem
                    key={`${item.name}-${index}`}
                    item={item}
                    index={index}
                    onEdit={() => handleEditBlock(index)}
                    onDelete={() => handleDeleteBlock(index)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {/* Add + Save buttons */}
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => handleAddBlock('user_input')}
              className="flex items-center gap-2 px-4 py-2 bg-sky-500/10 text-sky-400 rounded-lg text-sm hover:bg-sky-500/20"
            >
              <Plus className="w-4 h-4" /> Dane wejściowe
            </button>
            <button
              onClick={() => handleAddBlock('instruction')}
              className="flex items-center gap-2 px-4 py-2 bg-warning/10 text-warning rounded-lg text-sm hover:bg-warning/20"
            >
              <Plus className="w-4 h-4" /> Instrukcja
            </button>
            <button
              onClick={() => handleAddBlock('field')}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 text-emerald-400 rounded-lg text-sm hover:bg-emerald-500/20"
            >
              <Plus className="w-4 h-4" /> Pole AI
            </button>
            <button
              onClick={() => handleAddBlock('choice')}
              className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 text-amber-400 rounded-lg text-sm hover:bg-amber-500/20"
            >
              <Plus className="w-4 h-4" /> Wybór
            </button>
            <button
              onClick={() => handleAddBlock('image_gen')}
              className="flex items-center gap-2 px-4 py-2 bg-fuchsia-500/10 text-fuchsia-400 rounded-lg text-sm hover:bg-fuchsia-500/20"
            >
              <Plus className="w-4 h-4" /> Obraz AI
            </button>
            <button
              onClick={async () => {
                if (!(await confirmDialog({
                  title: 'Zresetuj pipeline',
                  message: 'Czy na pewno chcesz zresetować pipeline do domyślnych ustawień? To usunie wszystkie Twoje zmiany.',
                  confirmLabel: 'Zresetuj',
                }))) return;
                try {
                  const res = await labApi.resetPipeline(activeTab);
                  const items = res.data.data?.items || [];
                  setPipelineItems(items as PipelineItem[]);
                  setBuilderPage(res.data.data?.builderPage || defaultBuilderPageForPipeline(activeTab));
                } catch (err) { console.error(err); }
              }}
              className="flex items-center gap-2 px-3 py-2 text-text-muted hover:text-danger text-sm"
              title="Resetuj do domyślnych"
            >
              Resetuj
            </button>
          </div>
        </div>
      )}

      {/* Sticky save bar */}
      <div className="sticky bottom-0 z-30 -mx-1 px-1 pt-4">
        <div className="flex items-center gap-3 p-4 rounded-xl bg-surface-raised/95 backdrop-blur-sm border border-border shadow-lg">
          <button onClick={handleSavePipeline} disabled={savingPipeline} className="btn-primary text-sm flex items-center gap-1.5">
            {savingPipeline ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Zapisz ustawienia
          </button>
          {saveStatus === 'success' && (
            <span className="flex items-center gap-1.5 text-xs text-success animate-fade-in">
              <CheckCircle className="w-3.5 h-3.5" /> Zapisano
            </span>
          )}
          {saveStatus === 'error' && (
            <span className="flex items-center gap-1.5 text-xs text-danger animate-fade-in">
              <AlertCircle className="w-3.5 h-3.5" /> Błąd zapisu
            </span>
          )}
        </div>
      </div>

      {/* Edit Block Modal */}
      {editingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-surface-raised rounded-xl border border-border w-full max-w-2xl max-h-[80vh] overflow-y-auto p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4">
              {editingIndex === -1 ? 'Nowy blok' : 'Edytuj blok'}
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={labelClass}>Typ</label>
                  <select
                    value={editingItem.type}
                    onChange={e => setEditingItem({ ...editingItem, type: e.target.value as PipelineItem['type'] })}
                    className={inputClass}
                    disabled={editingIndex !== -1}
                  >
                    <option value="user_input">Dane wejściowe (formularz)</option>
                    <option value="instruction">Instrukcja (niewidoczna)</option>
                    <option value="field">Pole AI (widoczne)</option>
                    <option value="choice">Wybór (użytkownik wybiera)</option>
                    <option value="image_gen">Obraz AI</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Identyfikator</label>
                  <input
                    type="text"
                    value={editingItem.name}
                    onChange={e => setEditingItem({ ...editingItem, name: e.target.value.replace(/\s/g, '_') })}
                    className={inputClass}
                    placeholder="np. product_description"
                  />
                </div>
              </div>

              {activeTab === 'KNOWLEDGE' && editingItem.type !== 'instruction' && (
                <div>
                  <label className={labelClass}>Podkategoria Bazy wiedzy</label>
                  <select
                    value={editingItem.group || ''}
                    onChange={e => setEditingItem({ ...editingItem, group: e.target.value || null })}
                    className={inputClass}
                  >
                    <option value="">Brak podkategorii</option>
                    {editingItem.type === 'user_input' && (
                      <option value={KNOWLEDGE_INPUT_GROUP}>{KNOWLEDGE_INPUT_GROUP}</option>
                    )}
                    {KNOWLEDGE_PIPELINE_GROUPS.map(group => (
                      <option key={group} value={group}>{group}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* user_input specific fields */}
              {editingItem.type === 'user_input' && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Typ pola</label>
                      <select
                        value={editingItem.inputType || 'text'}
                        onChange={e => setEditingItem({ ...editingItem, inputType: e.target.value as 'text' | 'textarea' | 'link' | 'file' | 'packages' | 'select' })}
                        className={inputClass}
                      >
                        <option value="text">Krótki tekst</option>
                        <option value="textarea">Długi tekst</option>
                        <option value="link">Link URL</option>
                        <option value="file">Załącznik</option>
                        <option value="packages">Pakiety cenowe</option>
                        <option value="select">Lista wyboru</option>
                      </select>
                    </div>
                    <div className="flex items-end gap-3 pb-1 flex-wrap">
                      <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingItem.required || false}
                          onChange={e => setEditingItem({ ...editingItem, required: e.target.checked })}
                          className="rounded border-border-strong bg-surface-hover text-accent-primary focus:ring-accent-primary/30"
                        />
                        Pole wymagane
                      </label>
                      <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editingItem.allowMultiple || false}
                          onChange={e => setEditingItem({ ...editingItem, allowMultiple: e.target.checked })}
                          className="rounded border-border-strong bg-surface-hover text-accent-primary focus:ring-accent-primary/30"
                        />
                        Wiele wartości (+)
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Etykieta pola</label>
                    <input
                      type="text"
                      value={editingItem.label || ''}
                      onChange={e => setEditingItem({ ...editingItem, label: e.target.value })}
                      className={inputClass}
                      placeholder="np. Opis produktu"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Tekst pomocniczy</label>
                    <input
                      type="text"
                      value={editingItem.helperText || ''}
                      onChange={e => setEditingItem({ ...editingItem, helperText: e.target.value })}
                      className={inputClass}
                      placeholder="np. Opisz w kilku zdaniach czym jest Twój produkt..."
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Placeholder</label>
                    <input
                      type="text"
                      value={editingItem.placeholder || ''}
                      onChange={e => setEditingItem({ ...editingItem, placeholder: e.target.value })}
                      className={inputClass}
                      placeholder="np. Wpisz opis produktu..."
                    />
                  </div>
                  {editingItem.inputType === 'select' && (
                    <div>
                      <label className={labelClass}>Opcje listy wyboru</label>
                      <textarea
                        value={(editingItem.options || [])
                          .map(option => {
                            if (typeof option === 'string') return option;
                            return `${option.value} | ${option.label}`;
                          })
                          .join('\n')}
                        onChange={e => setEditingItem({
                          ...editingItem,
                          options: e.target.value
                            .split('\n')
                            .map(line => line.trim())
                            .filter(Boolean)
                            .map(line => {
                              const [value, ...labelParts] = line.split('|').map(part => part.trim());
                              const label = labelParts.join(' | ') || value;
                              return { value, label };
                            }),
                        })}
                        className={`${inputClass} min-h-[120px]`}
                        placeholder={'best_practices | Najlepsze praktyki\nreference_match | Message-match do referencji'}
                      />
                      <p className="text-[11px] text-text-muted mt-1">
                        Jedna opcja w linii. Format: wartość | etykieta.
                      </p>
                    </div>
                  )}
                  <div className="border-t border-border pt-4 mt-2">
                    <h4 className="text-sm font-medium text-sky-400 mb-3">Konfiguracja kroku wizarda</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className={labelClass}>Tytuł kroku</label>
                        <input
                          type="text"
                          value={editingItem.panelTitle || ''}
                          onChange={e => setEditingItem({ ...editingItem, panelTitle: e.target.value })}
                          className={inputClass}
                          placeholder="np. Informacje o produkcie"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Opis kroku</label>
                        <input
                          type="text"
                          value={editingItem.panelDescription || ''}
                          onChange={e => setEditingItem({ ...editingItem, panelDescription: e.target.value })}
                          className={inputClass}
                          placeholder="np. Podaj podstawowe dane o produkcie..."
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Tekst Cofnij</label>
                        <input
                          type="text"
                          value={editingItem.backLabel || ''}
                          onChange={e => setEditingItem({ ...editingItem, backLabel: e.target.value })}
                          className={inputClass}
                          placeholder="np. Wróć"
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Tekst CTA / Dalej</label>
                        <input
                          type="text"
                          value={editingItem.ctaLabel || ''}
                          onChange={e => setEditingItem({ ...editingItem, ctaLabel: e.target.value })}
                          className={inputClass}
                          placeholder="np. Dalej / Generuj bazę wiedzy"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className={labelClass}>Ekran wizarda</label>
                        <input
                          type="text"
                          value={editingItem.screenKey || ''}
                          onChange={e => setEditingItem({ ...editingItem, screenKey: e.target.value })}
                          className={inputClass}
                          placeholder="np. competitor_1"
                        />
                        <p className="text-[11px] text-text-muted mt-1">
                          Pola z tym samym identyfikatorem ekranu pojawią się razem na jednym kroku instalatora.
                        </p>
                      </div>
                      {editingItem.inputType === 'file' && (
                        <div className="col-span-2">
                          <label className={labelClass}>Dozwolone rozszerzenia</label>
                          <input
                            type="text"
                            value={editingItem.fileAccept || ''}
                            onChange={e => setEditingItem({ ...editingItem, fileAccept: e.target.value })}
                            className={inputClass}
                            placeholder=".md,.csv"
                          />
                        </div>
                      )}
                      <div className="col-span-2">
                        <label className={labelClass}>Auto-wypełnij z pola produktu</label>
                        <select
                          value={editingItem.autoFillFrom || ''}
                          onChange={e => setEditingItem({ ...editingItem, autoFillFrom: e.target.value || '' })}
                          className={inputClass}
                        >
                          {autoFillOptions.map(option => (
                            <option key={option.value || 'none'} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* field/choice specific fields */}
              {(editingItem.type === 'field' || editingItem.type === 'choice' || editingItem.type === 'image_gen') && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Tytuł</label>
                      <input
                        type="text"
                        value={editingItem.title || ''}
                        onChange={e => setEditingItem({ ...editingItem, title: e.target.value })}
                        className={inputClass}
                        placeholder="np. Buyer Persona"
                      />
                    </div>
                    <div>
                      <label className={labelClass}>Ikona (emoji)</label>
                      <input
                        type="text"
                        value={editingItem.icon || ''}
                        onChange={e => setEditingItem({ ...editingItem, icon: e.target.value })}
                        className={inputClass}
                        placeholder="np. 👤"
                      />
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>AI shortcode</label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-text-muted">[</span>
                      <input
                        type="text"
                        value={getPipelineAiShortcode(editingItem)}
                        onChange={e => setEditingItem({
                          ...editingItem,
                          aiShortcode: e.target.value.replace(/[\[\]\s]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, ''),
                          ai_shortcode: undefined,
                        })}
                        className={inputClass}
                        placeholder="np. lab_krotki_opis_produktu"
                      />
                      <span className="text-sm text-text-muted">]</span>
                    </div>
                    <p className="text-[10px] text-text-muted mt-1">
                      Tego identyfikatora użyjesz w szablonach Kreatora, np. [lab_krotki_opis_produktu].
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className={labelClass}>Kategoria wyświetlania</label>
                      <input
                        type="text"
                        value={editingItem.displayGroup || ''}
                        onChange={e => setEditingItem({ ...editingItem, displayGroup: e.target.value || null })}
                        className={inputClass}
                        placeholder="np. Produkt, Persona, PODSUMOWANIE"
                      />
                      <p className="text-[10px] text-text-muted mt-1">
                        Używane tylko w gotowym widoku bazy wiedzy. Puste pole użyje grupy generowania.
                      </p>
                    </div>
                    <div>
                      <label className={labelClass}>Kolejność wyświetlania</label>
                      <input
                        type="number"
                        value={editingItem.displayOrder ?? ''}
                        onChange={e => setEditingItem({
                          ...editingItem,
                          displayOrder: e.target.value === '' ? null : Number(e.target.value),
                        })}
                        className={inputClass}
                        placeholder="np. 10"
                      />
                      <p className="text-[10px] text-text-muted mt-1">
                        Niższy numer pokazuje sekcję wyżej. Nie wpływa na kolejność generowania.
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Komunikaty postępu</label>
                    <textarea
                      value={(editingItem.progressMessages || []).join('\n')}
                      onChange={e => setEditingItem({
                        ...editingItem,
                        progressMessages: e.target.value
                          .split('\n')
                          .map(line => line.trim())
                          .filter(Boolean),
                      })}
                      rows={3}
                      className={`${inputClass} resize-y`}
                      placeholder="Jeden komunikat na linię, np. Analizuję źródła i jakość danych..."
                    />
                    <p className="text-[10px] text-text-muted mt-1">
                      Te teksty pokazują się użytkownikowi pod nagłówkiem generowania dla tego konkretnego kroku.
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingItem.hidden || false}
                      onChange={e => setEditingItem({ ...editingItem, hidden: e.target.checked })}
                      className="rounded border-border-strong bg-surface-hover text-accent-primary focus:ring-accent-primary/30"
                    />
                    Ukryj sekcję w widoku użytkownika
                  </label>
                </>
              )}

              {editingItem.type === 'image_gen' && (
                <div className="space-y-3 p-3 bg-surface-hover/50 rounded-lg border border-border-subtle">
                  <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Konfiguracja obrazu</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className={labelClass}>Rozmiar</label>
                      <select
                        value={editingItem.imageSize || '1024x1024'}
                        onChange={e => setEditingItem({ ...editingItem, imageSize: e.target.value as PipelineItem['imageSize'] })}
                        className={inputClass}
                      >
                        <option value="1024x1024">Kwadrat 1024x1024</option>
                        <option value="1024x1792">Pion 1024x1792</option>
                        <option value="1792x1024">Poziom 1792x1024</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Styl obrazu</label>
                      <select
                        value={editingItem.imageStyle || 'natural'}
                        onChange={e => setEditingItem({ ...editingItem, imageStyle: e.target.value as PipelineItem['imageStyle'] })}
                        className={inputClass}
                      >
                        <option value="natural">Natural</option>
                        <option value="vivid">Vivid</option>
                      </select>
                    </div>
                    <div>
                      <label className={labelClass}>Liczba obrazów</label>
                      <input
                        type="number"
                        min={1}
                        max={12}
                        value={editingItem.imageCount || 1}
                        onChange={e => setEditingItem({ ...editingItem, imageCount: Math.max(1, Math.min(12, Number(e.target.value) || 1)) })}
                        className={inputClass}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* AI Config per step — for field/choice */}
              {(editingItem.type === 'field' || editingItem.type === 'choice') && (
                <div className="space-y-3 p-3 bg-surface-hover/50 rounded-lg border border-border-subtle">
                  <h4 className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Konfiguracja AI kroku</h4>

                  {/* Research Level */}
                  <div>
                    <label className="text-xs text-text-secondary mb-1 block group relative cursor-help">
                      Web Research
                      <span className="invisible group-hover:visible absolute left-0 bottom-full mb-1 w-64 p-2 bg-surface-base border border-border rounded-lg text-[10px] text-text-secondary shadow-lg z-50">
                        Search = AI wyszukuje w internecie aktualne dane przed odpowiedzią (~10-30s). Deep Research = wieloetapowe badanie: AI generuje zapytania → przeszukuje internet → syntetyzuje wyniki (~2-5 min). Droższe ale daje aktualne dane rynkowe.
                      </span>
                    </label>
                    <div className="flex gap-2">
                      {([
                        { value: 'off', label: 'Wyłączony', desc: 'Tylko wiedza modelu' },
                        { value: 'search', label: 'Search', desc: 'Szybkie wyszukiwanie (google_search / web_search)' },
                        { value: 'deep_research', label: 'Deep Research', desc: 'Wieloetapowy research (~2-5 min)' },
                      ] as const).map(opt => {
                        const currentLevel = editingItem.researchLevel || (editingItem.deepResearch ? 'search' : 'off');
                        const isActive = currentLevel === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setEditingItem({ ...editingItem, researchLevel: opt.value, deepResearch: opt.value !== 'off' })}
                            className={`flex-1 px-2 py-2 rounded-lg text-xs text-center border transition-all ${
                              isActive
                                ? opt.value === 'deep_research'
                                  ? 'bg-amber-500/15 border-amber-500/30 text-amber-400'
                                  : opt.value === 'search'
                                    ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400'
                                    : 'bg-surface-hover/50 border-border-strong text-text-primary'
                                : 'bg-surface-raised border-border-subtle text-text-muted hover:border-border'
                            }`}
                          >
                            <div className="font-medium">{opt.label}</div>
                            <div className="text-[10px] mt-0.5 opacity-70">{opt.desc}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Model Override */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-text-secondary mb-1 block group relative cursor-help">
                        Provider (override)
                        <span className="invisible group-hover:visible absolute left-0 bottom-full mb-1 w-56 p-2 bg-surface-base border border-border rounded-lg text-[10px] text-text-secondary shadow-lg z-50">
                          Wybierz dostawcę dla tego kroku, jeśli nie ustawiono przypisania czynności ani domyślnego dostawcy w panelu AI superadmina. Centralne ustawienia mają pierwszeństwo.
                        </span>
                      </label>
                      <Select
                        value={editingItem.modelProvider || ''}
                        onChange={e => setEditingItem({ ...editingItem, modelProvider: e.target.value || undefined, modelId: undefined })}
                        className="w-full px-3 py-1.5 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50"
                      >
                        <option value="">Domyślny (z ustawień)</option>
                        <option value="gemini">Gemini</option>
                        <option value="chatgpt">ChatGPT</option>
                        <option value="claude">Claude</option>
                        <option value="kimi">Kimi</option>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-text-secondary mb-1 block group relative cursor-help">
                        Model (override)
                        <span className="invisible group-hover:visible absolute left-0 bottom-full mb-1 w-56 p-2 bg-surface-base border border-border rounded-lg text-[10px] text-text-secondary shadow-lg z-50">
                          Wybierz konkretny model. Fast = szybkie i tanie. Balanced = optymalny. Premium = najwyższa jakość. Reasoning = modele z rozumowaniem (wolniejsze, do złożonych analiz).
                        </span>
                      </label>
                      {(() => {
                        const models = editingItem.modelProvider ? (STEP_MODELS[editingItem.modelProvider] || []) : [];
                        const selectedModel = models.find(m => m.id === editingItem.modelId);
                        return (
                          <>
                            <select
                              value={editingItem.modelId || ''}
                              onChange={e => setEditingItem({ ...editingItem, modelId: e.target.value || undefined })}
                              className="w-full px-3 py-1.5 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50"
                            >
                              <option value="">Domyślny (z ustawień)</option>
                              {models.map(m => {
                                const ti = STEP_TIER_INFO[m.tier];
                                return (
                                  <option key={m.id} value={m.id}>
                                    {ti?.icon} {m.label} — {m.cost} — {m.bestFor}
                                  </option>
                                );
                              })}
                            </select>
                            {selectedModel && (
                              <div className={`mt-1.5 flex items-center gap-2 px-2 py-1 rounded-lg ${STEP_TIER_INFO[selectedModel.tier]?.bg} border border-border-subtle`}>
                                <span className={`text-[10px] font-medium ${STEP_TIER_INFO[selectedModel.tier]?.color}`}>
                                  {STEP_TIER_INFO[selectedModel.tier]?.label}
                                </span>
                                <span className="text-[10px] text-text-secondary">|</span>
                                <span className="text-[10px] text-text-secondary">input/output: {selectedModel.cost} za 1M tok.</span>
                                <span className="text-[10px] text-text-secondary">|</span>
                                <span className="text-[10px] text-text-secondary">{selectedModel.bestFor}</span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Model tier legend */}
                  {editingItem.modelProvider ? (
                    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
                      {Object.entries(STEP_TIER_INFO).map(([key, ti]) => (
                        <span key={key} className="flex items-center gap-1">
                          <span className={`inline-block w-1.5 h-1.5 rounded-full ${ti.bg} border ${ti.color.replace('text-', 'border-')}`} />
                          <span className={ti.color}>{ti.label} ({ti.icon})</span>
                          <span className="text-text-disabled">
                            {key === 'fast' && '— tanie, szybkie'}
                            {key === 'balanced' && '— koszt/jakość'}
                            {key === 'premium' && '— top jakość'}
                            {key === 'reasoning' && '— logika, analiza'}
                          </span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-[10px] text-text-disabled">Krok użyje globalnego providera i modelu z ustawień AI. Wybierz override żeby użyć innego modelu na tym kroku.</p>
                  )}

                  {/* Temperature Override */}
                  <div>
                    <label className="text-xs text-text-secondary mb-1 block group relative cursor-help">
                      Temperatura (override)
                      <span className="invisible group-hover:visible absolute left-0 bottom-full mb-1 w-64 p-2 bg-surface-base border border-border rounded-lg text-[10px] text-text-secondary shadow-lg z-50">
                        Kontroluje kreatywność odpowiedzi. 0 = deterministyczne, powtarzalne. 0.3-0.5 = precyzyjne (analizy, specyfikacje). 0.7 = balans (domyślne). 1.0+ = kreatywne (ad copy, nagłówki). Puste = wartość z ustawień providera.
                      </span>
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min={0}
                        max={2}
                        step={0.1}
                        value={editingItem.temperature ?? 0.7}
                        onChange={e => setEditingItem({ ...editingItem, temperature: parseFloat(e.target.value) })}
                        className="flex-1 accent-accent-primary h-1.5"
                      />
                      <div className="flex items-center gap-1.5">
                        <input
                          type="number"
                          min={0}
                          max={2}
                          step={0.1}
                          value={editingItem.temperature ?? ''}
                          onChange={e => setEditingItem({ ...editingItem, temperature: e.target.value ? parseFloat(e.target.value) : undefined })}
                          placeholder="def."
                          className="w-16 px-2 py-1.5 bg-surface-hover border border-border rounded-lg text-text-primary text-sm text-center focus:outline-none focus:border-accent-primary/50"
                        />
                        {editingItem.temperature != null && (
                          <button
                            type="button"
                            onClick={() => setEditingItem({ ...editingItem, temperature: undefined })}
                            className="p-1 text-text-muted hover:text-danger"
                            title="Resetuj do domyślnej"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-[10px] text-text-disabled mt-1">
                      {editingItem.temperature == null ? 'Użyje domyślnej temperatury z ustawień providera' :
                       editingItem.temperature <= 0.3 ? '❄️ Precyzyjne — analizy, specyfikacje, dane' :
                       editingItem.temperature <= 0.7 ? '⚖️ Balans — opisy, baza wiedzy' :
                       editingItem.temperature <= 1.2 ? '🔥 Kreatywne — ad copy, nagłówki, UGC' :
                       '🌋 Bardzo losowe — rzadko przydatne'}
                    </p>
                  </div>
                </div>
              )}

              {/* Prompt — for non-user_input types */}
              {editingItem.type !== 'user_input' && (
                <div>
                  <label className={labelClass}>Prompt</label>
                  <textarea
                    value={editingItem.prompt}
                    onChange={e => setEditingItem({ ...editingItem, prompt: e.target.value })}
                    rows={8}
                    className={`${inputClass} font-mono text-xs`}
                    placeholder="Treść promptu dla AI..."
                  />
                </div>
              )}

              {editingItem.type === 'choice' && (
                <div className="space-y-3">
                  <div>
                    <label className={labelClass}>Liczba opcji do wyboru</label>
                    <input
                      type="number"
                      min={2}
                      max={10}
                      value={editingItem.choiceCount || 5}
                      onChange={e => setEditingItem({ ...editingItem, choiceCount: parseInt(e.target.value) || 5 })}
                      className={inputClass}
                    />
                    <p className="text-xs text-text-muted mt-1">System pokaże tyle opcji użytkownikowi (2-10)</p>
                  </div>
                  <div>
                    <label className={labelClass}>Pobierz opcje z pola</label>
                    <input
                      type="text"
                      value={editingItem.optionsFromField || ''}
                      onChange={e => setEditingItem({ ...editingItem, optionsFromField: e.target.value })}
                      className={inputClass}
                      placeholder="np. solution_mechanism"
                    />
                    <p className="text-xs text-text-muted mt-1">Jeśli pole ma sekcję z propozycjami, wybór nie wykona dodatkowego calla AI.</p>
                  </div>
                  <div>
                    <label className={labelClass}>Dopisz wybór do pola</label>
                    <input
                      type="text"
                      value={editingItem.applyChoiceToField || ''}
                      onChange={e => setEditingItem({ ...editingItem, applyChoiceToField: e.target.value })}
                      className={inputClass}
                      placeholder="np. solution_mechanism"
                    />
                    <p className="text-xs text-text-muted mt-1">Po wyborze decyzja użytkownika zostanie dopisana do tej sekcji.</p>
                  </div>
                </div>
              )}

              {(editingItem.type === 'field' || editingItem.type === 'choice') && (
                <div>
                  <label className={labelClass}>Kontekst z (pola, oddzielone przecinkami; puste = wszystkie)</label>
                  <input
                    type="text"
                    value={editingItem.contextFrom?.join(', ') || ''}
                    onChange={e => {
                      const val = e.target.value.trim();
                      setEditingItem({
                        ...editingItem,
                        contextFrom: val ? val.split(',').map(s => s.trim()).filter(Boolean) : null,
                      });
                    }}
                    className={inputClass}
                    placeholder="np. summary, marketing_angle (puste = wszystkie poprzednie)"
                  />
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={handleSaveBlock}
                className="px-4 py-2 bg-accent-primary/20 text-accent-primary rounded-lg text-sm font-medium"
              >
                {editingIndex === -1 ? 'Dodaj' : 'Zapisz'}
              </button>
              <button
                onClick={() => { setEditingItem(null); setEditingIndex(-1); }}
                className="px-4 py-2 text-text-secondary hover:text-text-primary text-sm"
              >
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Lab Field Config (Admin) ────────────────────────────────────────────────

interface UnifiedField {
  key: string;
  label: string;
  type: string;
  icon?: string;
  showInTable: boolean;
  width?: number;
  isCustom?: boolean;
  allowDuplicate?: boolean;
}

interface FieldOptionItem {
  value: string;
  label: string;
  color: string;
}

const FIELD_TYPE_LABELS: Record<string, string> = {
  text: 'Tekst',
  textarea: 'Tekst wieloliniowy',
  select: 'Wybór (select)',
  url: 'Link URL',
  date: 'Data',
  status: 'Status',
  testStatus: 'Status testu',
  potential: 'Potencjał',
  transcripts: 'Transkrypcje',
  packages: 'Pakiety cenowe',
};

const CREATABLE_FIELD_TYPES = [
  { value: 'text', label: 'Tekst (input)' },
  { value: 'textarea', label: 'Tekst wieloliniowy' },
  { value: 'select', label: 'Wybór (select z kolorami)' },
  { value: 'url', label: 'Link URL' },
  { value: 'transcripts', label: 'Transkrypty reklam' },
  { value: 'packages', label: 'Pakiety cenowe' },
];

function SortableFieldItem({
  field,
  onToggleTable,
  onToggleDuplicate,
  onUpdateWidth,
  onDelete,
  onExpandOptions,
  isExpanded,
  fieldOptions,
  onAddOption,
  onUpdateOption,
  onRemoveOption,
}: {
  field: UnifiedField;
  onToggleTable: () => void;
  onToggleDuplicate: () => void;
  onUpdateWidth: (width: number) => void;
  onDelete: () => void;
  onExpandOptions: () => void;
  isExpanded: boolean;
  fieldOptions: FieldOptionItem[];
  onAddOption: () => void;
  onUpdateOption: (idx: number, updates: Partial<FieldOptionItem>) => void;
  onRemoveOption: (idx: number) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: field.key });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  const canDuplicateField = field.key !== 'ads' && field.key !== 'broadcast';
  const hasEditableOptions = field.type === 'select' || field.type === 'testStatus' || field.type === 'potential';
  const lockOptionValue = field.type === 'testStatus' || field.type === 'potential';

  return (
    <div ref={setNodeRef} style={style}>
      <div className="flex items-center gap-3 p-2.5 bg-surface-hover/50 rounded-lg hover:bg-surface-hover/80 transition-colors">
        <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-text-disabled hover:text-text-secondary">
          <GripVertical className="w-4 h-4" />
        </button>
        <button
          onClick={onToggleTable}
          className="shrink-0"
          title={field.showInTable ? 'Widoczne w tabeli' : 'Ukryte w tabeli'}
        >
          {field.showInTable
            ? <Star className="w-4 h-4 text-warning fill-yellow-400" />
            : <Star className="w-4 h-4 text-text-disabled hover:text-text-secondary" />
          }
        </button>
        <span className="text-sm text-text-primary flex-1">{field.label}</span>
        <span className="text-[10px] text-text-muted px-1.5 py-0.5 bg-surface-muted rounded">{FIELD_TYPE_LABELS[field.type] || field.type}</span>
        {canDuplicateField && (
          <label className="flex items-center gap-1 cursor-pointer shrink-0" title="Pozwól duplikować pole">
            <input
              type="checkbox"
              checked={!!field.allowDuplicate}
              onChange={onToggleDuplicate}
              className="w-3 h-3 rounded border-border-strong bg-surface-muted text-accent-primary focus:ring-0 focus:ring-offset-0 cursor-pointer"
            />
            <span className="text-[10px] text-text-muted">+</span>
          </label>
        )}
        {field.isCustom && <span className="text-[10px] text-accent-primary/60 px-1.5 py-0.5 bg-accent-primary/10 rounded">custom</span>}
        {field.showInTable && (
          <input
            type="number"
            value={field.width || 120}
            onChange={e => onUpdateWidth(parseInt(e.target.value) || 120)}
            className="w-14 px-1.5 py-0.5 bg-surface-muted border border-border rounded text-text-primary text-xs text-center"
            title="Szerokość (px)"
          />
        )}
        {hasEditableOptions && (
          <button onClick={onExpandOptions} className="p-1 text-text-muted hover:text-accent-primary" title="Opcje">
            <Pencil className="w-3 h-3" />
          </button>
        )}
        {field.isCustom && (
          <button onClick={onDelete} className="p-1 text-text-muted hover:text-danger">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      {isExpanded && hasEditableOptions && (
        <div className="ml-10 mt-2 mb-2 p-3 bg-surface-raised/50 rounded-lg border border-border-subtle space-y-2">
          {fieldOptions.map((opt, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                type="color"
                value={opt.color}
                onChange={e => onUpdateOption(idx, { color: e.target.value })}
                className="w-7 h-7 rounded cursor-pointer bg-transparent border-0"
              />
              <input
                type="text"
                value={opt.value}
                onChange={e => onUpdateOption(idx, { value: e.target.value, label: e.target.value })}
                disabled={lockOptionValue}
                className={`${lockOptionValue ? 'w-36 text-text-muted' : 'flex-1 text-text-primary'} px-2 py-1 bg-surface-hover border border-border rounded text-sm disabled:opacity-70`}
                placeholder="Wartość"
              />
              {lockOptionValue && (
                <input
                  type="text"
                  value={opt.label}
                  onChange={e => onUpdateOption(idx, { label: e.target.value })}
                  className="flex-1 px-2 py-1 bg-surface-hover border border-border rounded text-text-primary text-sm"
                  placeholder="Etykieta"
                />
              )}
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium"
                style={{ backgroundColor: `${opt.color}20`, color: opt.color }}
              >
                {opt.label || 'Podgląd'}
              </span>
              {!lockOptionValue && (
                <button onClick={() => onRemoveOption(idx)} className="p-1 text-text-muted hover:text-danger">
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
          {!lockOptionValue && (
            <button onClick={onAddOption} className="text-accent-primary text-xs hover:underline">+ Dodaj opcję</button>
          )}
        </div>
      )}
    </div>
  );
}

function AdminLabFieldsContent() {
  const [fields, setFields] = useState<UnifiedField[]>([]);
  const [fieldOptions, setFieldOptions] = useState<Record<string, FieldOptionItem[]>>({});
  const [savingFields, setSavingFields] = useState(false);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [expandedOptions, setExpandedOptions] = useState<string | null>(null);
  const [showNewField, setShowNewField] = useState(false);
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const [newFieldType, setNewFieldType] = useState('text');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    loadFieldConfig();
  }, []);

  const loadFieldConfig = async () => {
    try {
      const res = await labApi.getFieldConfig();
      const data = res.data.data;
      setFields(data.fields || []);
      setFieldOptions(data.fieldOptions || {});
    } catch {
      // Use defaults
    } finally {
      setFieldsLoading(false);
    }
  };

  const handleSaveFieldConfig = async () => {
    setSavingFields(true);
    try {
      await labApi.saveFieldConfig({ fields, fieldOptions });
    } catch (err) {
      console.error('Error saving field config:', err);
    } finally {
      setSavingFields(false);
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setFields(prev => {
      const oldIndex = prev.findIndex(f => f.key === active.id);
      const newIndex = prev.findIndex(f => f.key === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const toggleShowInTable = (key: string) => {
    setFields(prev => prev.map(f => f.key === key ? { ...f, showInTable: !f.showInTable } : f));
  };

  const toggleAllowDuplicate = (key: string) => {
    if (key === 'ads' || key === 'broadcast') return;
    setFields(prev => prev.map(f => f.key === key ? { ...f, allowDuplicate: !f.allowDuplicate } : f));
  };

  const updateFieldWidth = (key: string, width: number) => {
    setFields(prev => prev.map(f => f.key === key ? { ...f, width } : f));
  };

  const deleteField = (key: string) => {
    setFields(prev => prev.filter(f => f.key !== key));
    setFieldOptions(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const addCustomField = () => {
    if (!newFieldLabel.trim()) return;
    const key = `custom_${newFieldLabel.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;
    if (fields.find(f => f.key === key)) return;
    setFields(prev => [...prev, { key, label: newFieldLabel.trim(), type: newFieldType, showInTable: false, isCustom: true }]);
    if (newFieldType === 'select') {
      setFieldOptions(prev => ({ ...prev, [key]: [] }));
    }
    setNewFieldLabel('');
    setNewFieldType('text');
    setShowNewField(false);
  };

  const addOption = (fieldKey: string) => {
    setFieldOptions(prev => ({
      ...prev,
      [fieldKey]: [...(prev[fieldKey] || []), { value: '', label: '', color: '#6b7280' }],
    }));
  };

  const updateOption = (fieldKey: string, idx: number, updates: Partial<FieldOptionItem>) => {
    setFieldOptions(prev => ({
      ...prev,
      [fieldKey]: (prev[fieldKey] || []).map((o, i) => i === idx ? { ...o, ...updates } : o),
    }));
  };

  const removeOption = (fieldKey: string, idx: number) => {
    setFieldOptions(prev => ({
      ...prev,
      [fieldKey]: (prev[fieldKey] || []).filter((_, i) => i !== idx),
    }));
  };

  if (fieldsLoading) return <div className="text-center text-text-muted py-12">Ładowanie...</div>;

  return (
    <div className="space-y-6">
      <div className="bg-surface-raised/50 rounded-xl border border-border-subtle p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-text-primary">Pola produktu</h3>
            <p className="text-sm text-text-muted mt-1">Przeciągaj, aby zmienić kolejność. Gwiazdka = widoczne w tabeli. Checkbox (+) = pozwól duplikować pole.</p>
          </div>
          <button
            onClick={() => setShowNewField(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-accent-primary/20 text-accent-primary rounded-lg text-xs font-medium hover:bg-accent-primary/30 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Nowe pole
          </button>
        </div>

        {showNewField && (
          <div className="mb-4 p-3 bg-surface-hover/50 rounded-lg border border-accent-primary/20 space-y-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={newFieldLabel}
                onChange={e => setNewFieldLabel(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addCustomField()}
                placeholder="Nazwa pola..."
                className="flex-1 px-3 py-1.5 bg-surface-hover border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-accent-primary/50"
                autoFocus
              />
              <select
                value={newFieldType}
                onChange={e => setNewFieldType(e.target.value)}
                className="px-3 py-1.5 bg-surface-hover border border-border rounded-lg text-text-primary text-sm"
              >
                {CREATABLE_FIELD_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => { setShowNewField(false); setNewFieldLabel(''); }} className="px-3 py-1.5 text-text-secondary text-xs hover:text-text-primary">Anuluj</button>
              <button onClick={addCustomField} disabled={!newFieldLabel.trim()} className="px-3 py-1.5 bg-accent-primary/20 text-accent-primary rounded-lg text-xs font-medium disabled:opacity-50">Dodaj</button>
            </div>
          </div>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={fields.map(f => f.key)} strategy={verticalListSortingStrategy}>
            <div className="space-y-1.5">
              {fields.map(field => (
                <SortableFieldItem
                  key={field.key}
                  field={field}
                  onToggleTable={() => toggleShowInTable(field.key)}
                  onToggleDuplicate={() => toggleAllowDuplicate(field.key)}
                  onUpdateWidth={(w) => updateFieldWidth(field.key, w)}
                  onDelete={() => deleteField(field.key)}
                  onExpandOptions={() => setExpandedOptions(expandedOptions === field.key ? null : field.key)}
                  isExpanded={expandedOptions === field.key}
                  fieldOptions={fieldOptions[field.key] || []}
                  onAddOption={() => addOption(field.key)}
                  onUpdateOption={(idx, updates) => updateOption(field.key, idx, updates)}
                  onRemoveOption={(idx) => removeOption(field.key, idx)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      </div>

      {/* Save */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveFieldConfig}
          disabled={savingFields}
          className="flex items-center gap-2 px-4 py-2 bg-accent-primary/20 text-accent-primary rounded-lg text-sm font-medium disabled:opacity-50 hover:bg-accent-primary/30 transition-colors"
        >
          {savingFields ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Zapisz konfigurację pól
        </button>
      </div>
    </div>
  );
}

// ─── Admin Grafiki Systemowe ─────────────────────────────────────────────────
// Cross-tenant graphics dla każdej instancji EcomFlow (placeholdery, miniatury,
// ikony domyślne). System-scoped — każdy nowy superadmin po reset tenanta widzi
// te same grafiki.

function AdminSystemAssetsContent() {
  const [assets, setAssets] = useState<SystemAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const confirmDialog = useConfirmDialog();

  const reload = useCallback(() => {
    setLoading(true);
    superAdminApi.listSystemAssets()
      .then(res => setAssets(res.data))
      .catch(() => setError('Nie udało się pobrać listy grafik'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setError(null);
    try {
      await superAdminApi.uploadSystemAsset(selectedFile, displayName.trim() || undefined);
      setSelectedFile(null);
      setDisplayName('');
      reload();
    } catch (err) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? 'Nie udało się wgrać pliku';
      setError(message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (asset: SystemAsset) => {
    const ok = await confirmDialog({
      title: 'Usunąć grafikę?',
      message: `"${asset.displayName ?? asset.originalName}" - tej akcji nie można cofnąć. Jeśli grafika jest używana w szablonach, miejsca te pokażą zepsuty obraz.`,
      confirmLabel: 'Usuń',
    });
    if (!ok) return;
    try {
      await superAdminApi.deleteSystemAsset(asset.id);
      reload();
    } catch {
      setError('Nie udało się usunąć grafiki');
    }
  };

  const copyUrl = (asset: SystemAsset) => {
    const url = `/api/uploads/system/${asset.filename}`;
    navigator.clipboard.writeText(url);
    setCopiedId(asset.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-text-primary">Grafiki systemowe</h2>
        <p className="text-sm text-text-secondary mt-1">
          Pliki dostępne dla każdej instancji EcomFlow. Używaj jako placeholdery, miniatury szablonów, ikony domyślne. URL pliku skopiuj i wstaw tam, gdzie potrzebujesz.
        </p>
      </div>

      {/* Upload form */}
      <div className="p-4 rounded-xl bg-surface-raised border border-border-subtle">
        <h3 className="text-sm font-medium text-text-primary mb-3 flex items-center gap-2">
          <Upload className="w-4 h-4" /> Dodaj grafikę
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-[1fr,1fr,auto] gap-3 items-end">
          <div>
            <label className="block text-xs text-text-secondary mb-1">Plik (max 5MB, png/jpg/gif/webp/svg)</label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/gif,image/webp,image/svg+xml"
              onChange={e => setSelectedFile(e.target.files?.[0] ?? null)}
              className="w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-accent-primary/15 file:text-accent-primary file:cursor-pointer file:text-xs"
            />
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1">Nazwa wyświetlana (opcjonalnie)</label>
            <input
              type="text"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="np. Domyślna miniatura"
              maxLength={200}
              className="w-full px-2 py-1.5 bg-surface-hover border border-border rounded text-xs"
            />
          </div>
          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="btn-primary text-xs flex items-center gap-1.5 px-4 py-2 disabled:opacity-50"
          >
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            Wgraj
          </button>
        </div>
        {error && (
          <p className="mt-2 text-xs text-danger flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5" /> {error}
          </p>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-accent-primary" />
        </div>
      ) : assets.length === 0 ? (
        <EmptyState
          icon={ImageIcon}
          title="Brak grafik systemowych"
          description="Wgraj pierwszą grafikę powyżej. Pojawi się ona tutaj i będzie dostępna dla każdej instancji EcomFlow."
          size="sm"
        />
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {assets.map(asset => {
            const url = `/api/uploads/system/${asset.filename}`;
            return (
              <div key={asset.id} className="rounded-xl bg-surface-raised border border-border-subtle overflow-hidden flex flex-col">
                <div className="aspect-square bg-surface-hover flex items-center justify-center overflow-hidden">
                  <img
                    src={url}
                    alt={asset.displayName ?? asset.originalName}
                    className="max-w-full max-h-full object-contain"
                    loading="lazy"
                  />
                </div>
                <div className="p-2.5 flex-1 flex flex-col">
                  <p className="text-xs font-medium text-text-primary truncate" title={asset.displayName ?? asset.originalName}>
                    {asset.displayName ?? asset.originalName}
                  </p>
                  <p className="text-[10px] text-text-muted mt-0.5">
                    {formatSize(asset.size)} · {asset.mimeType.split('/')[1]?.toUpperCase()}
                  </p>
                  <div className="flex items-center gap-1.5 mt-2">
                    <button
                      onClick={() => copyUrl(asset)}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1 rounded bg-surface-hover hover:bg-surface-hover/80 text-[10px] text-text-secondary hover:text-text-primary transition-colors"
                      title="Skopiuj URL"
                    >
                      {copiedId === asset.id ? <Check className="w-3 h-3 text-success" /> : <Copy className="w-3 h-3" />}
                      {copiedId === asset.id ? 'Skopiowano' : 'Kopiuj URL'}
                    </button>
                    <button
                      onClick={() => handleDelete(asset)}
                      className="flex items-center justify-center px-2 py-1 rounded bg-surface-hover hover:bg-danger/15 text-[10px] text-text-secondary hover:text-danger transition-colors"
                      title="Usuń"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Admin Szablony Kreatora ─────────────────────────────────────────────────
// System-scoped templates manager. Cross-tenant, każdy nowy superadmin widzi
// te same szablony. Drag-drop reorder dla isSystem, promote dla tenant
// templates, edycja nazwy + thumbnail, "wyczyść wszystkie" jako bulk.

type TemplateCategoryFilter = 'all' | 'general' | 'product' | 'other';

function AdminPageTemplatesContent() {
  const [templates, setTemplates] = useState<PageTemplateAdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState<TemplateCategoryFilter>('all');
  const [error, setError] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const confirmDialog = useConfirmDialog();

  const reload = useCallback(() => {
    setLoading(true);
    superAdminApi.listAllPageTemplates()
      .then(res => setTemplates(res.data))
      .catch(() => setError('Nie udało się pobrać listy szablonów'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(
    () => templates.filter(t => (
      categoryFilter === 'all' || normalizeTemplateCategory(t.category) === categoryFilter
    )),
    [templates, categoryFilter],
  );
  const systemTemplates = useMemo(() => filtered.filter(t => t.isSystem), [filtered]);
  const tenantTemplates = useMemo(() => filtered.filter(t => !t.isSystem), [filtered]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = systemTemplates.findIndex(t => t.id === active.id);
    const newIndex = systemTemplates.findIndex(t => t.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(systemTemplates, oldIndex, newIndex);
    // Optimistic — update local state, potem persist
    setTemplates(prev => {
      const otherFilters = prev.filter(t => !t.isSystem || (categoryFilter !== 'all' && normalizeTemplateCategory(t.category) !== categoryFilter));
      const updatedSystem = reordered.map((t, i) => ({ ...t, displayOrder: i }));
      return [...updatedSystem, ...otherFilters];
    });

    try {
      await superAdminApi.reorderSystemPageTemplates(
        reordered.map((t, i) => ({ id: t.id, displayOrder: i })),
      );
    } catch {
      setError('Nie udało się zapisać kolejności');
      reload();
    }
  };

  const handleRename = async (id: string, name: string) => {
    if (!name.trim()) return;
    try {
      await superAdminApi.updatePageTemplate(id, { name: name.trim() });
      setTemplates(prev => prev.map(t => t.id === id ? { ...t, name: name.trim() } : t));
    } catch {
      setError('Nie udało się zmienić nazwy');
    }
  };

  const handleThumbnailChange = async (id: string, thumbnail: string) => {
    const value = thumbnail.trim() || null;
    try {
      await superAdminApi.updatePageTemplate(id, { thumbnail: value });
      setTemplates(prev => prev.map(t => t.id === id ? { ...t, thumbnail: value } : t));
    } catch {
      setError('Nie udało się zaktualizować miniatury');
    }
  };

  const handleScopeChange = async (id: string, scope: 'PAGE' | 'SECTION' | 'ROW') => {
    try {
      await superAdminApi.updatePageTemplate(id, { scope });
      setTemplates(prev => prev.map(t => t.id === id ? { ...t, scope } : t));
    } catch {
      setError('Nie udało się zmienić zakresu');
    }
  };

  const handleCategoryChange = async (id: string, category: string) => {
    const normalized = normalizeTemplateCategory(category);
    try {
      await superAdminApi.updatePageTemplate(id, { category: normalized });
      setTemplates(prev => prev.map(t => t.id === id ? { ...t, category: normalized } : t));
    } catch {
      setError('Nie udało się zmienić kategorii');
    }
  };

  const handleDelete = async (template: PageTemplateAdminRow) => {
    const ok = await confirmDialog({
      title: 'Usunąć szablon?',
      message: `"${template.name}" - tej akcji nie można cofnąć.`,
      confirmLabel: 'Usuń',
    });
    if (!ok) return;
    try {
      await superAdminApi.deletePageTemplate(template.id);
      reload();
    } catch {
      setError('Nie udało się usunąć szablonu');
    }
  };

  const handlePromote = async (template: PageTemplateAdminRow) => {
    const ok = await confirmDialog({
      title: 'Promować do systemowego?',
      message: `"${template.name}" stanie się szablonem systemowym - widocznym dla każdej instancji EcomFlow. Tracisz powiązanie z tenantem właściciela.`,
      confirmLabel: 'Promuj',
    });
    if (!ok) return;
    try {
      await superAdminApi.promotePageTemplate(template.id);
      reload();
    } catch {
      setError('Nie udało się promować szablonu');
    }
  };

  const handleClearAll = async () => {
    const count = templates.filter(t => t.isSystem).length;
    if (count === 0) return;
    const ok = await confirmDialog({
      title: 'Wyczyścić bibliotekę?',
      message: `Usunie wszystkie ${count} szablonów systemowych. Szablony tenantów (prywatne) nie są ruszane. Tej akcji nie można cofnąć.`,
      confirmLabel: 'Wyczyść wszystkie systemowe',
    });
    if (!ok) return;
    try {
      await superAdminApi.clearAllSystemPageTemplates();
      reload();
    } catch {
      setError('Nie udało się wyczyścić biblioteki');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">Szablony Kreatora</h2>
          <p className="text-sm text-text-secondary mt-1">
            Szablony systemowe są widoczne w bibliotece każdego użytkownika EcomFlow. Panel zapisuje je do pliku systemowego, a baza jest tylko mirrorem dla edytora.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setImportOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-primary/15 hover:bg-accent-primary/25 text-xs text-accent-primary transition-colors"
          >
            <FileUp className="w-3.5 h-3.5" /> Importuj JSON
          </button>
          <button
            onClick={reload}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-surface-hover hover:bg-surface-hover/80 text-xs text-text-secondary hover:text-text-primary transition-colors"
            title="Odśwież listę"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Odśwież
          </button>
          <button
            onClick={handleClearAll}
            disabled={systemTemplates.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-danger/15 hover:bg-danger/25 text-xs text-danger disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" /> Wyczyść systemowe
          </button>
        </div>
      </div>

      {importOpen && (
        <ImportTemplateModal
          onClose={() => setImportOpen(false)}
          onImported={() => { setImportOpen(false); reload(); }}
        />
      )}

      {/* Category filter */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 bg-surface-raised/50 p-1 rounded-xl border border-border-subtle w-fit">
          {([{ value: 'all', label: 'Wszystkie' }, ...TEMPLATE_CATEGORIES] as Array<{ value: TemplateCategoryFilter; label: string }>).map(opt => (
            <button
              key={opt.value}
              onClick={() => setCategoryFilter(opt.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                categoryFilter === opt.value
                  ? 'bg-accent-info/20 text-accent-info border border-accent-info/30'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover/50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-danger/10 border border-danger/30 text-xs text-danger">
          <AlertCircle className="w-3.5 h-3.5" /> {error}
          <button onClick={() => setError(null)} className="ml-auto"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-accent-primary" />
        </div>
      ) : (
        <>
          {/* System templates - DnD reorderable */}
          <section>
            <h3 className="text-sm font-medium text-text-primary mb-2 flex items-center gap-2">
              <Star className="w-4 h-4 text-accent-primary" />
              Systemowe ({systemTemplates.length})
            </h3>
            {systemTemplates.length === 0 ? (
              <EmptyState
                icon={Layers}
                title="Brak szablonów systemowych"
                description={categoryFilter === 'all'
                  ? 'Promuj szablon z sekcji "Z tenantów" poniżej, żeby udostępnić go wszystkim instancjom.'
                  : `Brak systemowych szablonów w kategorii ${templateCategoryLabel(categoryFilter)}.`}
                size="sm"
              />
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={systemTemplates.map(t => t.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-2">
                    {systemTemplates.map(template => (
                      <SortableTemplateRow
                        key={template.id}
                        template={template}
                        onRename={(name) => handleRename(template.id, name)}
                        onThumbnailChange={(thumb) => handleThumbnailChange(template.id, thumb)}
                        onScopeChange={(scope) => handleScopeChange(template.id, scope)}
                        onCategoryChange={(category) => handleCategoryChange(template.id, category)}
                        onDelete={() => handleDelete(template)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
          </section>

          {/* Tenant templates - flat list, with Promote action */}
          <section>
            <h3 className="text-sm font-medium text-text-primary mb-2 flex items-center gap-2">
              <Layers className="w-4 h-4 text-text-secondary" />
              Z tenantów ({tenantTemplates.length})
            </h3>
            {tenantTemplates.length === 0 ? (
              <EmptyState
                icon={Layers}
                title="Brak szablonów od tenantów"
                description="Tenanci tworzą tu szablony przez 'Save as template' w edytorze. Możesz je promować do systemowych."
                size="sm"
              />
            ) : (
              <div className="space-y-2">
                {tenantTemplates.map(template => (
                  <TenantTemplateRow
                    key={template.id}
                    template={template}
                    onPromote={() => handlePromote(template)}
                    onDelete={() => handleDelete(template)}
                  />
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function SortableTemplateRow({ template, onRename, onThumbnailChange, onScopeChange, onCategoryChange, onDelete }: {
  template: PageTemplateAdminRow;
  onRename: (name: string) => void;
  onThumbnailChange: (thumbnail: string) => void;
  onScopeChange: (scope: 'PAGE' | 'SECTION' | 'ROW') => void;
  onCategoryChange: (category: string) => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: template.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  const [name, setName] = useState(template.name);
  const [thumbnail, setThumbnail] = useState(template.thumbnail ?? '');

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-3 p-3 rounded-xl bg-surface-raised border border-border-subtle">
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-text-muted hover:text-text-secondary touch-none"
        aria-label="Przeciągnij aby zmienić kolejność"
      >
        <GripVertical className="w-4 h-4" />
      </button>

      {/* Thumbnail preview */}
      <div className="w-14 h-14 rounded-lg bg-surface-hover overflow-hidden flex items-center justify-center shrink-0">
        {template.thumbnail ? (
          <img src={template.thumbnail} alt={template.name} className="max-w-full max-h-full object-contain" loading="lazy" />
        ) : (
          <Layers className="w-5 h-5 text-text-muted" />
        )}
      </div>

      {/* Name + thumbnail URL inputs */}
      <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-2 gap-2">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => name !== template.name && onRename(name)}
          maxLength={200}
          placeholder="Nazwa szablonu"
          className="px-2 py-1 bg-surface-hover border border-border rounded text-xs"
        />
        <input
          type="text"
          value={thumbnail}
          onChange={(e) => setThumbnail(e.target.value)}
          onBlur={() => thumbnail !== (template.thumbnail ?? '') && onThumbnailChange(thumbnail)}
          placeholder="URL miniatury (np. /api/uploads/system/...)"
          className="px-2 py-1 bg-surface-hover border border-border rounded text-xs"
        />
      </div>

      {/* Scope */}
      <select
        value={template.scope}
        onChange={(e) => onScopeChange(e.target.value as 'PAGE' | 'SECTION' | 'ROW')}
        className="px-2 py-1 bg-surface-hover border border-border rounded text-xs shrink-0"
      >
        <option value="PAGE">Strona</option>
        <option value="SECTION">Sekcja</option>
        <option value="ROW">Rząd</option>
      </select>

      <select
        value={normalizeTemplateCategory(template.category)}
        onChange={(e) => onCategoryChange(e.target.value)}
        className="px-2 py-1 bg-surface-hover border border-border rounded text-xs shrink-0"
      >
        {TEMPLATE_CATEGORIES.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>

      <button
        onClick={onDelete}
        className="p-1.5 rounded hover:bg-danger/15 text-text-secondary hover:text-danger transition-colors"
        title="Usuń"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function TenantTemplateRow({ template, onPromote, onDelete }: {
  template: PageTemplateAdminRow;
  onPromote: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-surface-raised border border-border-subtle">
      <div className="w-14 h-14 rounded-lg bg-surface-hover overflow-hidden flex items-center justify-center shrink-0">
        {template.thumbnail ? (
          <img src={template.thumbnail} alt={template.name} className="max-w-full max-h-full object-contain" loading="lazy" />
        ) : (
          <Layers className="w-5 h-5 text-text-muted" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-text-primary truncate">{template.name}</p>
        <p className="text-[11px] text-text-muted mt-0.5">
          {template.scope} · {templateCategoryLabel(template.category)} · {template.tenant?.name ?? 'Tenant nieznany'} · {new Date(template.createdAt).toLocaleDateString('pl-PL')}
        </p>
      </div>
      <button
        onClick={onPromote}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-accent-primary/15 hover:bg-accent-primary/25 text-xs text-accent-primary transition-colors"
        title="Promuj do systemowych"
      >
        <ArrowUp className="w-3.5 h-3.5" /> Promuj
      </button>
      <button
        onClick={onDelete}
        className="p-1.5 rounded hover:bg-danger/15 text-text-secondary hover:text-danger transition-colors"
        title="Usuń"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function ImportTemplateModal({ onClose, onImported }: {
  onClose: () => void;
  onImported: () => void;
}) {
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'PAGE' | 'SECTION' | 'ROW'>('PAGE');
  const [category, setCategory] = useState('general');
  const [thumbnail, setThumbnail] = useState('');
  const [parsedContent, setParsedContent] = useState<unknown[] | null>(null);
  const [fileName, setFileName] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParseError(null);
    setParsedContent(null);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string);
        if (!Array.isArray(parsed)) {
          setParseError('Plik musi zawierać tablicę bloków JSON (eksport z edytora).');
          return;
        }
        if (parsed.length === 0) {
          setParseError('Tablica bloków jest pusta.');
          return;
        }
        setParsedContent(parsed);
        if (!name) {
          setName(file.name.replace(/\.json$/i, ''));
        }
      } catch {
        setParseError('Nieprawidłowy format JSON.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const canSubmit = parsedContent !== null && name.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !parsedContent) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await superAdminApi.importPageTemplate({
        name: name.trim(),
        scope,
        category: category.trim() || 'general',
        content: parsedContent,
        thumbnail: thumbnail.trim() || null,
      });
      onImported();
    } catch (err) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
        ?? 'Nie udało się zaimportować szablonu';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-surface-raised rounded-2xl border border-border shadow-2xl w-full max-w-lg mx-4 animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2">
            <FileUp className="w-4 h-4" /> Importuj szablon z JSON
          </h2>
          <button onClick={onClose} className="p-1 text-text-secondary hover:text-text-primary">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          <div>
            <label className="block text-xs text-text-secondary mb-1">Plik JSON (eksport z edytora)</label>
            <input
              type="file"
              accept="application/json,.json"
              onChange={handleFileChange}
              className="w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-accent-primary/15 file:text-accent-primary file:cursor-pointer file:text-xs"
            />
            {fileName && parsedContent && (
              <p className="text-[11px] text-success mt-1 flex items-center gap-1">
                <Check className="w-3 h-3" /> {fileName} - {parsedContent.length} block(ów) gotowe
              </p>
            )}
            {parseError && (
              <p className="text-[11px] text-danger mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> {parseError}
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs text-text-secondary mb-1">Nazwa szablonu</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              placeholder="np. Hero Section z CTA"
              className="w-full px-2 py-1.5 bg-surface-hover border border-border rounded text-xs"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-text-secondary mb-1">Zakres</label>
              <select
                value={scope}
                onChange={(e) => setScope(e.target.value as 'PAGE' | 'SECTION' | 'ROW')}
                className="w-full px-2 py-1.5 bg-surface-hover border border-border rounded text-xs"
              >
                <option value="PAGE">Strona</option>
                <option value="SECTION">Sekcja</option>
                <option value="ROW">Rząd</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-text-secondary mb-1">Kategoria</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-2 py-1.5 bg-surface-hover border border-border rounded text-xs"
              >
                {TEMPLATE_CATEGORIES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs text-text-secondary mb-1">URL miniatury (opcjonalnie)</label>
            <input
              type="text"
              value={thumbnail}
              onChange={(e) => setThumbnail(e.target.value)}
              placeholder="/api/uploads/system/..."
              className="w-full px-2 py-1.5 bg-surface-hover border border-border rounded text-xs"
            />
            <p className="text-[10px] text-text-muted mt-1">URL skopiuj z zakładki "Grafiki" (przycisk Kopiuj URL).</p>
          </div>

          {submitError && (
            <div className="flex items-center gap-2 p-2 rounded bg-danger/10 border border-danger/30 text-xs text-danger">
              <AlertCircle className="w-3.5 h-3.5" /> {submitError}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2 p-4 border-t border-border">
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="btn-primary text-xs flex items-center gap-1.5 px-4 py-2 disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileUp className="w-3.5 h-3.5" />}
            Wgraj jako szablon systemowy
          </button>
          <button
            onClick={onClose}
            className="text-xs px-3 py-2 text-text-secondary hover:text-text-primary transition-colors"
          >
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}
